import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, FileText, Calendar, Zap, Loader, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import { patchRequest } from '../../api/api';
import adsPlatformApi from '../../api/adsPlatform';
import { sanitizeOptimizationDescription } from '../../utils/sanitizeOptimizationDescription';
import { getEvidenceTablesForModal, isSplitTopSellersWhenRoasVeryHighRule, isExecutableActionType, isGoogleAdsPlatformUi } from '../../utils/optimizationTransformers';
import EvidenceTableModal from './EvidenceTableModal';
import { useAuth } from 'components/context/AuthContext';
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../utils/currency';
import CriteriaWithBars from '../optimizations/subcomponents/CriteriaWithBars';
import ValueChangeBlock from '../optimizations/subcomponents/ValueChangeBlock';
import CriteriaCheckmarkRow from '../optimizations/subcomponents/CriteriaCheckmarkRow';
import ItemValueChangeRow from '../optimizations/subcomponents/ItemValueChangeRow';
import BeforeAfter from '../optimizations/subcomponents/BeforeAfter';
import EntityTable from '../optimizations/subcomponents/EntityTable';
import MetricSingle from '../optimizations/subcomponents/MetricSingle';
import StatusPill from '../optimizations/subcomponents/StatusPill';
import ItemCard from '../optimizations/subcomponents/ItemCard';
import ContextMetricsBar from '../optimizations/subcomponents/ContextMetricsBar';
import UserTextAccordion from '../optimizations/subcomponents/UserTextAccordion';
import { getIntentTone } from '../optimizations/subcomponents/intentTone';
import CreativePreview from '../optimizations/subcomponents/CreativePreview';
import WindowComparisonCard from '../optimizations/subcomponents/WindowComparisonCard';
import CpaStreakChart from '../optimizations/subcomponents/CpaStreakChart';
import RoasStreakChart from '../optimizations/subcomponents/RoasStreakChart';
import {
  buildCpaStreakFromDailySeries,
  buildRoasStreakFromDailySeries,
} from '../optimizations/subcomponents/streakUtils';
import CpaWindowsChart from '../optimizations/subcomponents/CpaWindowsChart';
import RoasWindowsChart from '../optimizations/subcomponents/RoasWindowsChart';
import CtrWindowsChart from '../optimizations/subcomponents/CtrWindowsChart';
import DiagnosticScoreCard from '../optimizations/subcomponents/DiagnosticScoreCard';
import PlacementsSubItemsTable, {
  isPlacementsSubItems,
} from '../optimizations/subcomponents/PlacementsSubItemsTable';

const DESCRIPTION_P_CLASS = 'text-white/90 text-sm leading-relaxed';

const BASE_UI_METRIC_KEYS = new Set(['spend', 'roas', 'cpa', 'conversions']);

const SubItemsTable = ({
  subItems,
  subItemsSummary,
  currency = 'USD',
  primaryKpi = 'ROAS',
  selectedIds = null,
  onToggle = null,
}) => {
  if (!Array.isArray(subItems) || subItems.length === 0) return null;
  const summary = subItemsSummary && typeof subItemsSummary === 'object' ? subItemsSummary : null;
  const isMultiCurrency = summary?.currency === 'Múltiples monedas';
  const selectable = Array.isArray(selectedIds) && typeof onToggle === 'function';
  const selectedSet = selectable ? new Set(selectedIds.map((x) => String(x))) : null;
  const selectedCount = selectable ? selectedSet.size : (summary?.selected_count ?? summary?.total_count ?? subItems.length);
  const totalCount = summary?.total_count ?? subItems.length;
  return (
    <div className="mt-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs tracking-wide text-white/60 font-semibold">Items sugeridos</div>
          <div className="mt-0.5 text-[11px] text-white/55">
            {selectable
              ? 'Tildá los ítems que querés aplicar.'
              : 'Revisá los elementos que están afectando performance.'}
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center rounded-full border border-white/10 bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white/80 whitespace-nowrap">
          {selectedCount}/{totalCount} seleccionados
        </span>
      </div>

      <div className="mt-3 grid grid-cols-6 gap-3 text-[10px] uppercase tracking-wider text-white/45">
        <div className="col-span-2 truncate">Item</div>
        <div className="truncate">Tipo</div>
        <div className="text-right">Gasto</div>
        <div className="text-right">{String(primaryKpi || 'ROAS')}</div>
        <div className="text-right">Conv.</div>
      </div>

      <div className="mt-2 space-y-2">
        {subItems.slice(0, 8).map((it, idx) => {
          const name = it?.name || it?.keyword || it?.term || 'Item';
          const type = it?.type || 'ITEM';
          const metrics = it?.metrics || {};
          const spend = metrics?.spend ?? metrics?.cost ?? null;
          const roas = metrics?.roas ?? null;
          const cpa = metrics?.cpa ?? null;
          const conv = metrics?.conversions ?? null;
          const itemId = it?.id != null ? String(it.id) : null;
          const isChecked = selectable && itemId != null
            ? selectedSet.has(itemId)
            : (it?.selected_by_default !== false);
          return (
            <div key={`${itemId || name}-${idx}`} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
              <div className="grid grid-cols-6 gap-3 items-center">
                <div className="col-span-2 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {selectable && itemId != null ? (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggle(itemId)}
                        aria-label={`Incluir ${name}`}
                        className="h-4 w-4 shrink-0 rounded border-white/30 bg-white/5 accent-emerald-500 cursor-pointer"
                      />
                    ) : (
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${isChecked ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100' : 'border-white/10 bg-white/5 text-white/60'}`}>
                        {isChecked ? 'Incluido' : 'Opcional'}
                      </span>
                    )}
                    <div className="text-sm font-semibold text-white truncate" title={name}>{name}</div>
                  </div>
                </div>
                <div className="text-[11px] text-white/70 truncate" title={String(type)}>{String(type)}</div>
                <div className="text-right text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                  {spend != null ? formatCurrencyNoDecimals(spend, currency) : '—'}
                </div>
                <div className="text-right text-sm font-semibold tabular-nums whitespace-nowrap text-white">
                  {String(primaryKpi || '').toUpperCase() === 'CPA'
                    ? (cpa != null ? formatCurrencyNoDecimals(cpa, currency) : '—')
                    : (roas != null ? `${Number(roas).toFixed(1)}x` : '—')}
                </div>
                <div className="text-right text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                  {conv != null ? conv : '—'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {summary?.total_cost != null || isMultiCurrency ? (
        <div className="mt-3 pt-2 border-t border-white/10">
          {isMultiCurrency ? (
            <span className="inline-flex items-center rounded-full border border-amber-400/35 bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-100 whitespace-nowrap">
              Múltiples monedas — totales no aplicables
            </span>
          ) : (
            <div className="flex justify-between items-center text-xs text-white/70">
              <span>Total gasto</span>
              <span className="font-semibold text-white tabular-nums">
                {formatCurrencyNoDecimals(summary.total_cost, summary.currency || currency)}
              </span>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const isBudgetActionType = (suggestionOrActionType) => {
  // Support both passing raw action_type string or the whole suggestion object.
  const suggestion =
    suggestionOrActionType && typeof suggestionOrActionType === 'object'
      ? suggestionOrActionType
      : null;
  const rawActionType = suggestion ? suggestion?.action_type : suggestionOrActionType;

  const normalizedType = String(rawActionType || '').trim().toLowerCase();
  if (normalizedType === 'increase_budget' || normalizedType === 'decrease_budget') return true;
  if (normalizedType.includes('budget')) return true;

  // Meta diagnostic suggestions can still carry numeric before/after budget changes.
  const actionLabel = String(suggestion?.action || '').toLowerCase();
  const hasBudgetNumbers =
    suggestion?.value_before != null || suggestion?.value_after != null;
  if (hasBudgetNumbers && actionLabel.includes('presupuesto')) return true;

  return false;
};

const D_FIELD_BY_ACTION_TYPE = {
  INCREASE_BUDGET: 'daily_budget',
  DECREASE_BUDGET: 'daily_budget',
  ADJUST_TARGET_ROAS: 'target_roas',
  ADJUST_TARGET_CPA: 'target_cpa',
  ADJUST_COST_CAP: 'cost_cap',
  ADJUST_BID_CAP: 'bid_cap',
  ADJUST_MIN_ROAS: 'min_roas',
  ADJUST_ADSET_BID_AMOUNT: 'adset_bid_amount',
};

const D_RATIO_FIELDS = new Set(['target_roas', 'min_roas']);

const buildDArchetypeExecutionParams = (suggestion) => {
  const actionType = String(suggestion?.action_type || '').trim().toUpperCase();
  const field = D_FIELD_BY_ACTION_TYPE[actionType];
  if (!field) return null;
  const currency = D_RATIO_FIELDS.has(field)
    ? null
    : (suggestion?.value_change_display?.currency
        || suggestion?.metrics_extended?.currency
        || null);
  return { currency, field };
};

const coerceNumberOrEmpty = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : '';
};

// §6.13bis + §6.14: prefer BE-emitted ordered list; honor [] as "hide the bar";
// fall through to local computation only when the field is null/missing/non-array.
const resolveMetricKeysToShow = (opt, criteria, fallbackBase) => {
  const fromBackend = Array.isArray(opt?.metric_keys_to_show) ? opt.metric_keys_to_show : null;
  if (fromBackend) return fromBackend.slice(0, 8);
  const base = fallbackBase || ['spend', 'conversions', 'cpa', 'roas'];
  const fromCriteria = (criteria || [])
    .map((c) => c?.metric_key)
    .filter((k) => k && !base.includes(k));
  return [...base, ...fromCriteria];
};

// Filters items[] down to what we should render: drops dismissed items
// (is_liked === false) and any item that duplicates the suggestion's
// primary_entity (BE may include the primary as a row in items[] when there
// are sibling entities affected; the primary is already shown in the header).
const resolveVisibleItems = (opt) => {
  const items = Array.isArray(opt?.items) ? opt.items : [];
  const primaryExtId = opt?.primary_entity?.external_id != null
    ? String(opt.primary_entity.external_id) : null;
  const primaryId = opt?.primary_entity?.id != null
    ? String(opt.primary_entity.id) : null;
  return items.filter((it) => {
    if (it?.is_liked === false) return false;
    const extId = it?.target_entity?.external_id != null
      ? String(it.target_entity.external_id) : null;
    const id = it?.target_entity?.id != null ? String(it.target_entity.id) : null;
    if (primaryExtId && extId && extId === primaryExtId) return false;
    if (primaryId && id && id === primaryId) return false;
    return true;
  });
};

const isKeywordMatchTypeSelectableAction = (suggestion) => {
  if (!suggestion) return false;
  if (!isGoogleAdsPlatformUi(suggestion?.platform)) return false;
  const actionType = String(suggestion?.action_type || '').trim().toUpperCase();
  return (
    actionType === 'ADD_KEYWORD' ||
    actionType === 'ADD_NEGATIVE_KEYWORD' ||
    actionType === 'CHANGE_KEYWORD_MATCH_TYPE'
  );
};

const UI_METRIC_LABELS = {
  current_roas: 'ROAS actual',
  target_roas: 'ROAS objetivo',
  budget_utilization_pct: 'Uso presupuesto',
  current_cpa: 'CPA actual',
  target_cpa: 'CPA objetivo',
  accumulated_spend: 'Gasto acumulado',
  ad_spend: 'Gasto anuncio',
  adset_daily_budget: 'Presupuesto diario Ad Set',
  impressions_3d: 'Impresiones 3d',
  bid_type: 'Tipo de puja',
  cost_cap: 'Cost Cap',
  roas_30d: 'ROAS 30d',
  impressions: 'Impresiones',
  clicks: 'Clics',
  conversions_value: 'Valor conversiones',
  cost: 'Costo',
  video_3_sec_views: 'Vistas 3s',
  video_3_sec_rate: 'Tasa 3s',
  thruplays: 'Thruplays',
  ad_roas: 'ROAS anuncio',
  adset_roas_avg: 'ROAS promedio Ad Set',
  ctr: 'CTR',
  inline_link_click_ctr: 'CTR',
  account_avg_ctr: 'CTR promedio cuenta',
  frequency: 'Frecuencia',
  ctr_7d: 'CTR 7d',
  ctr_prev_7d: 'CTR 7d previo',
  ctr_change_pct: 'Variación CTR',
  campaign_type: 'Tipo de campaña',
  conversions_7d: 'Conversiones 7d',
  learning_status: 'Estado aprendizaje',
  auction_overlap_pct: 'Overlap subastas',
  adset_metrics: 'Métricas Ad Sets',
  audience_size: 'Tamaño audiencia',
  cpm: 'CPM',
  cpa: 'CPA',
  cpa_change_direction: 'Tendencia CPA',
  outbound_clicks: 'Clics salientes',
  landing_page_views: 'Vistas landing',
  loss_pct: 'Pérdida',
  roas: 'ROAS',
  conversion_rate: 'Tasa conversión',
  sessions: 'Sesiones',
  purchases: 'Compras',
  historical_cpa: 'CPA histórico',
  spend: 'Gasto',
  reels_spend_share: 'Gasto en Reels',
  reels_ctr: 'CTR Reels',
  ctr_total_avg: 'CTR promedio cuenta',
};

const UI_METRIC_FORMATS = {
  current_roas: 'number',
  target_roas: 'number',
  budget_utilization_pct: 'percent',
  current_cpa: 'currency',
  target_cpa: 'currency',
  accumulated_spend: 'currency',
  ad_spend: 'currency',
  adset_daily_budget: 'currency',
  impressions_3d: 'number',
  bid_type: 'text',
  cost_cap: 'currency',
  roas_30d: 'number',
  impressions: 'number',
  clicks: 'number',
  conversions_value: 'currency',
  cost: 'currency',
  video_3_sec_views: 'number',
  video_3_sec_rate: 'percent',
  thruplays: 'number',
  ad_roas: 'number',
  adset_roas_avg: 'number',
  ctr: 'percent',
  inline_link_click_ctr: 'percent',
  account_avg_ctr: 'percent',
  frequency: 'number',
  ctr_7d: 'percent',
  ctr_prev_7d: 'percent',
  ctr_change_pct: 'percent',
  campaign_type: 'text',
  conversions_7d: 'number',
  learning_status: 'text',
  auction_overlap_pct: 'percent',
  adset_metrics: 'text',
  audience_size: 'number',
  cpm: 'currency',
  cpa: 'currency',
  cpa_change_direction: 'text',
  outbound_clicks: 'number',
  landing_page_views: 'number',
  loss_pct: 'percent',
  roas: 'number',
  conversion_rate: 'percent',
  sessions: 'number',
  purchases: 'number',
  historical_cpa: 'currency',
  spend: 'currency',
  reels_spend_share: 'percent',
  reels_ctr: 'percent',
  ctr_total_avg: 'percent',
};

const UI_METRIC_ORDER = [
  'current_roas',
  'target_roas',
  'budget_utilization_pct',
  'current_cpa',
  'target_cpa',
  'accumulated_spend',
  'ad_spend',
  'adset_daily_budget',
  'impressions',
  'clicks',
  'conversions_value',
  'cost',
  'impressions_3d',
  'outbound_clicks',
  'landing_page_views',
  'loss_pct',
  'ctr',
  'account_avg_ctr',
  'ctr_7d',
  'ctr_prev_7d',
  'ctr_change_pct',
  'frequency',
  'cpm',
  'cpa',
  'historical_cpa',
  'roas',
  'roas_30d',
  'conversion_rate',
  'sessions',
  'purchases',
  'video_3_sec_views',
  'video_3_sec_rate',
  'thruplays',
  'campaign_type',
  'conversions_7d',
  'learning_status',
  'auction_overlap_pct',
  'audience_size',
  'cpa_change_direction',
  'bid_type',
  'cost_cap',
  'reels_spend_share',
  'reels_ctr',
  'ctr_total_avg',
  'ad_roas',
  'adset_roas_avg',
  'adset_metrics',
];

const formatCurrency = (value, currencyCode = 'USD') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return formatCurrencyAmount(value, currencyCode, {
    locale: 'es-ES',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    display: 'code',
  });
};

const formatCurrencyNoDecimals = (value, currencyCode = 'USD') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return formatCurrencyAmount(Math.round(n), currencyCode, {
    locale: 'es-ES',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    display: 'code',
  });
};

const formatNumberRounded = (value) => {
  if (value === null || value === undefined) return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  const rounded = Math.round(numeric * 100) / 100;
  const decimals = Number.isInteger(rounded) ? 0 : 2;
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  const percentValue = Math.abs(numeric) <= 1 ? numeric * 100 : numeric;
  return `${percentValue.toFixed(2)}%`;
};

const formatRatioAsPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${(Number(value) * 100).toFixed(0)}%`;
};

const formatConversions = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const numeric = Number(value);
  const decimals = Number.isInteger(numeric) ? 0 : 2;
  return formatNumber(numeric, decimals);
};

const resolveCardSummaryMetrics = (suggestion) => {
  const items = Array.isArray(suggestion?.items) ? suggestion.items : [];
  const groupedItems = Array.isArray(suggestion?.campaign_groups?.[0]?.items)
    ? suggestion.campaign_groups[0].items
    : [];
  const firstItem = items[0] || groupedItems[0] || null;
  const itemMetrics = firstItem?.metrics && typeof firstItem.metrics === 'object'
    ? firstItem.metrics
    : null;
  const extended = suggestion?.metrics_extended && typeof suggestion.metrics_extended === 'object'
    ? suggestion.metrics_extended
    : null;

  const pickMetric = (key) => {
    const fromItem = itemMetrics?.[key];
    if (fromItem !== null && fromItem !== undefined) return fromItem;
    const fromExtended = extended?.[key];
    if (fromExtended !== null && fromExtended !== undefined) return fromExtended;
    return null;
  };

  return {
    spend: pickMetric('spend'),
    roas: pickMetric('roas'),
    cpa: pickMetric('cpa'),
    conversions: pickMetric('conversions'),
  };
};

const inferSuggestionChangeFormat = (suggestion) => {
  const actionType = String(suggestion?.action_type || '').toLowerCase();
  const actionLabel = String(suggestion?.action || '').toLowerCase();
  const combined = `${actionType} ${actionLabel}`;

  if (combined.includes('roas')) return 'roas_percent';
  if (combined.includes('budget') || combined.includes('cpa') || combined.includes('cost_cap') || combined.includes('bid')) {
    return 'currency';
  }
  return 'number';
};

const formatCardChangeValue = (value, format, currencyCode = 'USD') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  if (format === 'roas_percent') return formatRatioAsPercent(value);
  if (format === 'currency') return formatCurrencyNoDecimals(value, currencyCode);
  return formatNumberRounded(value);
};

const parseISODateOnly = (value) => {
  if (!value || typeof value !== 'string') return null;
  const datePart = value.split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
  return new Date(y, m - 1, d);
};

const parseCreatedAtDateOnly = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatShortDateEs = (date, includeYear = false) => {
  if (!date) return '-';
  try {
    const formatter = new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      ...(includeYear ? { year: 'numeric' } : {}),
    });
    return formatter.format(date).replace('.', '');
  } catch {
    return date.toLocaleDateString('es-ES');
  }
};

const formatRangeEs = (start, end) => {
  if (!start || !end) return '-';
  const includeYear = start.getFullYear() !== end.getFullYear();
  return `${formatShortDateEs(start, includeYear)} – ${formatShortDateEs(end, includeYear)}`;
};

const formatAnalyzedRangeEs = (start, end) => {
  if (!start || !end) return '-';
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `${start.getDate()} – ${formatShortDateEs(end, true)}`;
  }
  const includeYear = start.getFullYear() !== end.getFullYear();
  return `${formatShortDateEs(start, includeYear)} – ${formatShortDateEs(end, true)}`;
};

const formatDateParam = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatUiMetricValue = (key, value, currencyCode = 'USD') => {
  if (value === null || value === undefined) return '-';
  const format = UI_METRIC_FORMATS[key] || (typeof value === 'number' ? 'number' : 'text');
  if (format === 'currency') return formatCurrency(value, currencyCode);
  if (format === 'percent') return formatPercent(value);
  if (format === 'number') return formatNumberRounded(value);
  if (Array.isArray(value)) return value.join(' | ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const getUiMetricLabel = (key) => UI_METRIC_LABELS[key] || key.replace(/_/g, ' ');

const getOrderedUiMetricKeys = (keysSet) => {
  const keys = Array.from(keysSet);
  const ordered = UI_METRIC_ORDER.filter((key) => keysSet.has(key));
  const remaining = keys.filter((key) => !UI_METRIC_ORDER.includes(key)).sort();
  return [...ordered, ...remaining];
};

const formatActionValue = (actionChange, value, currencyCode = 'USD') => {
  if (!actionChange) return '-';
  if (value === null || value === undefined) return '-';
  if (actionChange.format === 'currency') return formatCurrency(value, currencyCode);
  if (actionChange.format === 'percent') return formatPercent(value);
  if (actionChange.format === 'roas_percent') return formatRatioAsPercent(value);
  return formatNumberRounded(value);
};

const getActionAfterValueClass = (actionChange) => {
  if (!actionChange) return 'text-green-300 font-semibold';
  const before = Number(actionChange.before);
  const after = Number(actionChange.after);
  if (Number.isFinite(before) && Number.isFinite(after)) {
    if (after < before) return 'text-amber-400 font-semibold';
    if (after > before) return 'text-green-300 font-semibold';
  }
  const label = String(actionChange.label || '').toLowerCase();
  if (label.includes('reduc')) return 'text-amber-400 font-semibold';
  if (label.includes('increment') || label.includes('aument')) return 'text-green-300 font-semibold';
  return 'text-green-300 font-semibold';
};

const ITEM_COLUMN_WIDTH_CLASS = 'min-w-0 w-[min(280px,100%)] max-w-[280px]';

/** Renders one suggestion's full content (campaign_groups/items, details, rating) for accordion panel. Description is shown once in the modal header, not here. */
const SuggestionBodyContent = ({
  opt,
  parentSectionTitle,
  getCurrencyCodeForSuggestion,
  valueAfter,
  onChangeValueAfter,
  keywordMatchType,
  onChangeKeywordMatchType,
  getRoasCellClassName,
  getCpaCellClassName,
  getRateHandler,
  getOrderedUiMetricKeys,
  getUiMetricLabel,
  formatUiMetricValue,
  formatActionValue,
  formatCurrency,
  formatRatioAsPercent,
  formatConversions,
  onApplySuccess,
  setEvidenceModal,
  ITEM_COLUMN_WIDTH_CLASS,
  BASE_UI_METRIC_KEYS,
}) => {
  const [discardSubmitting, setDiscardSubmitting] = useState(false);
  const [commitSubmitting, setCommitSubmitting] = useState(false);
  const hasGroupedCampaigns = Array.isArray(opt?.campaign_groups) && opt.campaign_groups.length > 0;
  const isMetaPlatform = String(opt?.platform || '').toLowerCase().includes('meta');
  const suggestionCurrencyCode = getCurrencyCodeForSuggestion(opt);
  const statusUpper = String(opt?.status || '').toUpperCase();
  const canDismissSuggestion =
    !opt?.status || ['NEW', 'SHOWN'].includes(statusUpper);
  const canCommitSuggestion =
    !opt?.status || ['NEW', 'SHOWN'].includes(statusUpper);
  const showBudgetInput = isBudgetActionType(opt) && isExecutableActionType(opt?.action_type);
  const showKeywordMatchTypeSelect = isKeywordMatchTypeSelectableAction(opt);
  const isArchetypeD = String(opt?.archetype || '').toUpperCase() === 'D';
  const v2Currency = opt?.metrics_extended?.currency || getCurrencyCodeForSuggestion(opt) || 'USD';

  const handleDismissClick = async (e) => {
    e?.stopPropagation?.();
    setDiscardSubmitting(true);
    try {
      const rate = getRateHandler(opt);
      if (rate) await rate(opt.id, 'negative', undefined);
    } finally {
      setDiscardSubmitting(false);
    }
  };

  return (
    <>
      {isArchetypeD && (
        <>
          <CriteriaWithBars
            title="POR QUÉ CALIFICA"
            criteria={Array.isArray(opt?.criteria) ? opt.criteria : []}
            metrics={opt?.metrics_extended && typeof opt.metrics_extended === 'object' ? opt.metrics_extended : null}
            currency={v2Currency}
            intentTone="emerald"
            accountBenchmarks={opt?.account_benchmarks || null}
          />
          <ValueChangeBlock valueChangeDisplay={opt?.value_change_display || null} intent={opt?.intent} />
        </>
      )}
      {showKeywordMatchTypeSelect && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <label className="text-xs text-white/60" htmlFor={`keyword-match-type-${opt?.id}`}>
            Concordancia
          </label>
          <select
            id={`keyword-match-type-${opt?.id}`}
            value={keywordMatchType || 'BROAD'}
            onChange={(e) => onChangeKeywordMatchType?.(e.target.value)}
            className="w-44 h-9 rounded-lg bg-white/10 border border-white/20 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
            aria-label="Tipo de concordancia para keywords"
          >
            <option value="BROAD">Amplia</option>
            <option value="PHRASE">Frase</option>
            <option value="EXACT">Exacta</option>
          </select>
        </div>
      )}
      {hasGroupedCampaigns ? (
        <div className="mb-4 space-y-4">
          {opt.campaign_groups.map((campaign, campaignIndex) => {
            const rawItems = Array.isArray(campaign.items) ? campaign.items : [];
            const items = rawItems.filter((it) => it?.is_liked !== false);
            const actionChange = items.find((item) => item.action_change)?.action_change || null;
            const hasActionValues = actionChange && (actionChange.before != null || actionChange.after != null);
            const showActionChange = Boolean(hasActionValues);
            const uiMetricKeys = new Set();
            items.forEach((item) => {
              const extendedMetrics = item.metrics_extended;
              if (!extendedMetrics || typeof extendedMetrics !== 'object') return;
              Object.entries(extendedMetrics).forEach(([key, value]) => {
                if (key === 'currency') return;
                if (value !== null && value !== undefined) uiMetricKeys.add(key);
              });
            });
            const orderedUiMetricKeysAll = getOrderedUiMetricKeys(uiMetricKeys);
            const useUiMetricsOnly = isMetaPlatform && orderedUiMetricKeysAll.length > 0;
            const hasGastoColumn = !useUiMetricsOnly || orderedUiMetricKeysAll.includes('spend');
            const orderedUiMetricKeys = orderedUiMetricKeysAll.filter((key) => {
              if (!useUiMetricsOnly && BASE_UI_METRIC_KEYS.has(key)) return false;
              if (hasGastoColumn && key === 'cost') return false;
              return true;
            });
            const isSingleCampaign = opt.campaign_groups.length === 1;
            const campaignNameTrimmed = (campaign.campaign_name || '').trim();
            const parentTitleTrimmed = (parentSectionTitle || '').trim();
            const suppressCampaignTitle = isSingleCampaign && parentTitleTrimmed !== '' && campaignNameTrimmed === parentTitleTrimmed;
            return (
              <div key={campaign.campaign_id || campaign.campaign_name || campaignIndex} className="backdrop-blur-md bg-white/10 border border-white/20 rounded-xl p-4">
                <div className="mb-3">
                  {!suppressCampaignTitle && (
                    <h3 className="text-sm font-semibold text-white">{campaign.campaign_name}</h3>
                  )}
                  {campaign.objective_name && (
                    <span className="inline-flex mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-500/20 text-green-100 border border-green-400/30">
                      {campaign.objective_name}
                    </span>
                  )}
                </div>
                {items.length > 0 ? (
                  (() => {
                    const firstType = items[0]?.type_label;
                    const allSameType = items.every((i) => (i.type_label || '') === (firstType || ''));
                    const firstColumnHeader = allSameType && firstType ? firstType : 'Item';
                    return items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm">
                      <thead>
                        <tr className="border-b border-white/30">
                          <th className={`px-2 sm:px-4 py-2 text-left text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider ${ITEM_COLUMN_WIDTH_CLASS}`}>{firstColumnHeader}</th>
                          {!useUiMetricsOnly && <th className="px-2 sm:px-4 py-2 text-center text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider">Gasto</th>}
                          {!useUiMetricsOnly && <th className="px-2 sm:px-4 py-2 text-center text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider">ROAS</th>}
                          {!useUiMetricsOnly && <th className="px-2 sm:px-4 py-2 text-center text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider">CPA</th>}
                          {!useUiMetricsOnly && <th className="px-2 sm:px-4 py-2 text-center text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider">Conv.</th>}
                          {showActionChange && <th className="px-2 sm:px-4 py-2 text-center text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider">{actionChange.label} actual</th>}
                          {showActionChange && <th className="px-2 sm:px-4 py-2 text-center text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider">{actionChange.label} sugerido</th>}
                          {orderedUiMetricKeys.map((key) => (
                            <th key={key} className="px-2 sm:px-4 py-2 text-center text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider">{getUiMetricLabel(key)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/20">
                        {items.map((item, idx) => (
                          <tr key={`${campaign.campaign_id || campaignIndex}-${idx}`} className="hover:bg-white/10 transition-colors">
                            <td className={`px-2 sm:px-4 py-2 text-white text-xs sm:text-sm ${ITEM_COLUMN_WIDTH_CLASS}`}>
                              <span className="truncate block min-w-0" title={item.name || ''}>{item.name || '-'}</span>
                              {isArchetypeD && item?.value_change_display ? (
                                <ItemValueChangeRow valueChangeDisplay={item.value_change_display} className="mt-1" />
                              ) : null}
                              {isArchetypeD && Array.isArray(opt?.criteria) && Array.isArray(item?.criteria_evaluation) ? (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {opt.criteria.map((c, cIdx) => {
                                    const evalRow = item.criteria_evaluation?.[cIdx] || null;
                                    return (
                                      <CriteriaCheckmarkRow
                                        key={`${item.id}-${c.metric_key}-${cIdx}`}
                                        label={c.label}
                                        actualValue={evalRow?.actual_value ?? c.actual_value}
                                        thresholdValue={evalRow?.threshold_value ?? c.threshold_value}
                                        formatHint={c.format_hint}
                                        currency={v2Currency}
                                        passes={evalRow?.passes_criterion ?? true}
                                      />
                                    );
                                  })}
                                </div>
                              ) : null}
                            </td>
                            {!useUiMetricsOnly && <td className="px-2 sm:px-4 py-2 text-center text-white/90 text-xs sm:text-sm whitespace-nowrap">{formatCurrency(item.metrics?.spend, suggestionCurrencyCode)}</td>}
                            {!useUiMetricsOnly && (
                              <td className="px-2 sm:px-4 py-2 text-center text-xs sm:text-sm whitespace-nowrap">
                                <span className={getRoasCellClassName(item, opt)}>
                                  {formatRatioAsPercent(item.metrics?.roas)}
                                </span>
                              </td>
                            )}
                            {!useUiMetricsOnly && (
                              <td className="px-2 sm:px-4 py-2 text-center text-xs sm:text-sm whitespace-nowrap">
                                <span className={getCpaCellClassName(item, opt)}>
                                  {formatCurrency(item.metrics?.cpa, suggestionCurrencyCode)}
                                </span>
                              </td>
                            )}
                            {!useUiMetricsOnly && <td className="px-4 py-2 text-center text-sm text-white/80">{formatConversions(item.metrics?.conversions)}</td>}
                            {showActionChange && <td className="px-4 py-2 text-center text-sm text-white/80">{formatActionValue(item.action_change, item.action_change?.before, suggestionCurrencyCode)}</td>}
                            {showActionChange && <td className={`px-4 py-2 text-center text-sm ${getActionAfterValueClass(item.action_change)}`}>{formatActionValue(item.action_change, item.action_change?.after, suggestionCurrencyCode)}</td>}
                            {orderedUiMetricKeys.map((key) => (
                              <td key={key} className="px-2 sm:px-4 py-2 text-center text-xs sm:text-sm text-white/80 whitespace-nowrap">{formatUiMetricValue(key, item.metrics_extended?.[key], suggestionCurrencyCode)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                    ) : (
                  <div className="text-xs text-white/60">Sin items relacionados.</div>
                );
                  })()
                ) : (
                  <div className="text-xs text-white/60">Sin items relacionados.</div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {(opt.action?.toLowerCase().includes('incrementar presupuesto') || opt.action?.toLowerCase().includes('aumentar presupuesto') || opt.daily_budget || opt.new_daily_budget) && (opt.value_before != null || opt.value_after != null) && (
            <div className="mb-4 overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/30">
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Daily Budget Actual</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-center text-xs font-bold text-white uppercase tracking-wider">Daily Budget Sugerido</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-white/10 transition-colors">
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-white text-xs sm:text-sm">
                      {opt.value_before != null ? formatCurrencyNoDecimals(opt.value_before, suggestionCurrencyCode) : '-'}
                    </td>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-green-300">
                                      {showBudgetInput ? (
                        <input
                          inputMode="decimal"
                          type="number"
                          min="0"
                          step="1"
                          value={valueAfter}
                          onChange={(e) => onChangeValueAfter?.(e.target.value)}
                          className="w-44 h-9 rounded-lg bg-white/10 border border-white/20 px-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-amber-400/40 text-center"
                          placeholder="0.00"
                          aria-label="Presupuesto sugerido"
                        />
                      ) : (
                        opt.value_after != null ? formatCurrencyNoDecimals(opt.value_after, suggestionCurrencyCode) : '-'
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {opt.details && opt.details.length > 0 && (
            <div className="mb-4 overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-white/30">
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-left text-xs font-bold text-white uppercase tracking-wider">{opt.details_label || 'Detalle'}</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-center text-xs font-bold text-white uppercase tracking-wider">Gasto</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-center text-xs font-bold text-white uppercase tracking-wider">ROAS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {opt.details.map((detail, idx) => (
                    <tr key={idx} className="hover:bg-white/10 transition-colors">
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-white text-xs sm:text-sm truncate" title={detail.name || detail.keyword || detail.term}>{detail.name || detail.keyword || detail.term}</td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-center text-white/90 text-xs sm:text-sm whitespace-nowrap">
                        {detail.spend != null ? formatCurrency(detail.spend, suggestionCurrencyCode) : '-'}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm whitespace-nowrap">
                        <span className={detail.roas != null && opt.target_roas != null && detail.roas < opt.target_roas ? 'text-red-300' : 'text-green-300'}>
                          {detail.roas != null ? `${(detail.roas * 100).toFixed(0)}%` : '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-3 sm:mt-4">
        {setEvidenceModal && (() => {
          const tables = getEvidenceTablesForModal(opt.evidence, suggestionCurrencyCode);
          if (tables.length === 0) return null;
          return (
            <button
              type="button"
              onClick={() => setEvidenceModal(tables)}
              className="inline-flex items-center gap-1 sm:gap-2 min-h-[44px] px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              title="Ver evidencia"
              aria-label="Ver evidencia"
            >
              <FileText className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" aria-hidden />
              <span className="hidden sm:inline">Ver evidencia</span>
              <span className="sm:hidden">Evidencia</span>
            </button>
          );
        })()}
        {(() => {
          const actionType = String(opt?.action_type || '').trim().toUpperCase();
          const isRefreshCreative = actionType === 'REFRESH_CREATIVE';
          const isSwitchToAdvantagePlus = actionType === 'SWITCH_TO_ADVANTAGE_PLUS';
          const isLandingPage = actionType === 'OPTIMIZE_LANDING_PAGE_SPEED' || actionType === 'IMPROVE_LANDING_PAGE_RELEVANCE';
          const isMetaReviewOnly = isRefreshCreative || isSwitchToAdvantagePlus || isLandingPage;

          if (isMetaReviewOnly) {
            return (
              <>
                {canDismissSuggestion && (
                  <button
                    type="button"
                    onClick={handleDismissClick}
                    disabled={discardSubmitting}
                    className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg border border-white/20 bg-white/10 text-white/85 hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-60 disabled:cursor-not-allowed"
                    title="Descartar sugerencia"
                  >
                    {discardSubmitting ? (
                      <Loader className="w-4 h-4 shrink-0 animate-spin" aria-hidden />
                    ) : null}
                    <span className="text-xs sm:text-sm font-semibold">Descartar</span>
                  </button>
                )}
            {canCommitSuggestion ? (
              <button
                type="button"
                onClick={async () => {
                  if (!opt?.id) return;
                  if (commitSubmitting) return;
                  setCommitSubmitting(true);
                  try {
                    const response = await patchRequest(`/api/suggestions/${opt.id}/apply`, { execute: false });
                    const data = response?.data || null;
                    if (data?.status === 'APPLIED') {
                      toast.success('Listo. La sugerencia quedó marcada como aplicada.');
                      onApplySuccess?.(opt.id, 'APPLIED');
                    } else {
                      toast.success('Listo.');
                    }
                  } catch (err) {
                    console.warn('No se pudo aplicar la sugerencia', opt?.id, err);
                    toast.error('No se pudo marcar como aplicada. Intentá de nuevo.');
                  } finally {
                    setCommitSubmitting(false);
                  }
                }}
                disabled={commitSubmitting}
                className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg border border-emerald-400/45 bg-gradient-to-r from-emerald-500/20 to-teal-500/15 text-emerald-50 hover:from-emerald-500/30 hover:to-teal-500/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {commitSubmitting ? (
                  <Loader className="w-4 h-4 shrink-0 animate-spin mr-2" aria-hidden />
                ) : null}
                <span className="text-xs sm:text-sm font-semibold">
                  {commitSubmitting ? 'Marcando…' : '¡Lo voy a hacer!'}
                </span>
              </button>
            ) : null}
              </>
            );
          }

          return (
            <>
              {canDismissSuggestion && (
                <button
                  type="button"
                  onClick={handleDismissClick}
                  disabled={discardSubmitting}
                  className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg border border-white/20 bg-white/10 text-white/85 hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Descartar sugerencia"
                >
                  {discardSubmitting ? (
                    <Loader className="w-4 h-4 shrink-0 animate-spin mr-2" aria-hidden />
                  ) : null}
                  <span className="text-xs sm:text-sm font-semibold">Descartar</span>
                </button>
              )}
            </>
          );
        })()}
      </div>
    </>
  );
};

const OptimizationDetailModalV2 = ({
  isOpen,
  onClose,
  optimization,
  allOptimizations = [],
  currentIndex = 0,
  onNavigate,
  onRatingSuccess,
  onApplySuccess,
  /** When set, modal shows all suggestions in current group as accordion; nav switches groups */
  allGroups = null,
  currentGroupIndex = 0,
  onNavigateGroup,
}) => {
  const { auth } = useAuth();
  const [currenciesData, setCurrenciesData] = useState(null);
  const activeBrand = auth?.user?.brands?.find((b) => b.id === auth?.user?.active_brand_id) || auth?.user?.active_brand || null;
  const currenciesByPlatform = currenciesData?.by_platform || {};
  const endpointMetaCurrency = normalizeCurrencyCode(currenciesByPlatform?.meta?.currency);
  const endpointGoogleCurrency = normalizeCurrencyCode(currenciesByPlatform?.google?.currency);
  const activeMetaCurrency = normalizeCurrencyCode(
    activeBrand?.meta_ad_account_currency ??
    activeBrand?.meta_account_currency ??
    activeBrand?.meta_currency ??
    auth?.user?.meta_ad_account_currency ??
    auth?.user?.meta_account_currency ??
    auth?.user?.meta_currency
  );
  const activeGoogleCurrency = normalizeCurrencyCode(
    activeBrand?.google_ads_account_currency ??
    activeBrand?.google_account_currency ??
    activeBrand?.google_currency ??
    auth?.user?.google_ads_account_currency ??
    auth?.user?.google_account_currency ??
    auth?.user?.google_currency
  );
  const metaActIdRaw = activeBrand?.meta_ad_account_id || auth?.user?.meta_ad_account_id || null;
  const metaActId = metaActIdRaw != null ? String(metaActIdRaw).replace(/^act_/, '').trim() : null;
  const googleCustomerIdRaw = activeBrand?.google_ads_customer_id || auth?.user?.google_ads_customer_id || null;
  const googleCustomerId = googleCustomerIdRaw != null ? String(googleCustomerIdRaw).replace(/\D/g, '').trim() : null;

  useEffect(() => {
    if (!auth?.token) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await adsPlatformApi.getCurrencies();
        const payload = response?.data?.data || response?.data || null;
        if (!cancelled) setCurrenciesData(payload);
      } catch {
        // Fallback to account/auth currencies already available in context.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [auth?.token]);

  const buildAdsManagerUrl = useCallback((params = {}) => {
    if (!metaActId) return null;
    const {
      selectedAdIds,
      entityName,
      dateStart,
      dateEnd,
    } = params;

    const qs = new URLSearchParams();
    qs.set('act', metaActId);
    qs.set('nav_source', 'no_referrer');
    qs.set('sort', 'spend~0');

    const start = formatDateParam(dateStart);
    const end = formatDateParam(dateEnd);
    if (start && end) {
      const range = `${start}_${end}`;
      qs.set('date', range);
      qs.set('insights_date', range);
    }

    if (entityName) {
      // Filter by ad/entity name in Ads Manager.
      const filterSet = {
        filters: [
          { field: 'ad.name', operator: 'CONTAIN', value: String(entityName) },
        ],
      };
      qs.set('filter_set', JSON.stringify(filterSet));
    }

    const ids = Array.isArray(selectedAdIds)
      ? selectedAdIds.map((id) => String(id || '').trim()).filter(Boolean)
      : selectedAdIds
        ? [String(selectedAdIds).trim()]
        : [];
    if (ids.length > 0) {
      const csv = ids.join(',');
      qs.set('selected_ad_ids', csv);
    }

    return `https://adsmanager.facebook.com/adsmanager/manage/ads?${qs.toString()}`;
  }, [metaActId]);

  const openAdsManager = useCallback((params) => {
    const url = buildAdsManagerUrl(params);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [buildAdsManagerUrl]);

  const buildGoogleAdsUrl = useCallback((params = {}) => {
    if (!googleCustomerId) return null;
    const { campaignId } = params;
    const qs = new URLSearchParams();
    qs.set('ocid', googleCustomerId);
    if (campaignId != null && String(campaignId).trim() !== '') {
      qs.set('campaignId', String(campaignId).trim());
    }
    return `https://ads.google.com/aw/campaigns?${qs.toString()}`;
  }, [googleCustomerId]);

  const openSuggestionDestination = useCallback((opt, params = {}) => {
    const permalinkUrl = opt?.primary_entity?.permalink_url;
    if (typeof permalinkUrl === 'string' && permalinkUrl.trim() !== '') {
      window.open(permalinkUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const platform = String(opt?.platform || '').toLowerCase();
    if (platform.includes('meta')) {
      openAdsManager({
        selectedAdIds: params.adExternalId ?? opt?.primary_entity_external_id ?? null,
        entityName: params.entityName ?? opt?.primary_entity_name ?? opt?.campaign_name ?? null,
        dateStart: params.dateStart ?? null,
        dateEnd: params.dateEnd ?? null,
      });
      return;
    }

    if (platform.includes('google')) {
      const campaignId = params.campaignId ?? opt?.campaign_groups?.[0]?.campaign_id ?? null;
      const url = buildGoogleAdsUrl({ campaignId });
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [openAdsManager, buildGoogleAdsUrl]);

  /** Sub-modal for evidence (e.g. related_search_terms). Array of { title, entries }. */
  const [evidenceModal, setEvidenceModal] = useState(null);
  const [groupDiscardSubmitting, setGroupDiscardSubmitting] = useState(false);
  const [groupCommitSubmitting, setGroupCommitSubmitting] = useState(false);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!isOpen && onCloseRef.current) onCloseRef.current();
  }, [isOpen]);

  const groupMode = Array.isArray(allGroups) && allGroups.length > 0 && typeof onNavigateGroup === 'function';
  const currentGroup = groupMode ? allGroups[currentGroupIndex] : null;
  const suggestionsInView = useMemo(() => {
    if (groupMode && currentGroup) {
      return Array.isArray(currentGroup) ? currentGroup : (currentGroup?.cards ?? []);
    }
    if (allOptimizations.length > 0) return allOptimizations;
    return optimization ? [optimization] : [];
  }, [groupMode, currentGroup, allOptimizations, optimization]);
  const normalizedRuleKey = useMemo(() => {
    const groupRuleKey = groupMode && currentGroup && !Array.isArray(currentGroup) ? (currentGroup?.ruleKey ?? null) : null;
    const firstSuggestionRuleKey = suggestionsInView[0]?.rule_key ?? null;
    const resolvedRuleKey = groupRuleKey ?? firstSuggestionRuleKey;
    return typeof resolvedRuleKey === 'string' ? resolvedRuleKey.toLowerCase() : null;
  }, [groupMode, currentGroup, suggestionsInView]);
  const currentOptimization = optimization || suggestionsInView[0] || null;
  const groupHasCommitableSuggestions = useMemo(() => {
    const opts = Array.isArray(suggestionsInView) ? suggestionsInView : [];
    return opts.some((s) => {
      const st = String(s?.status || '').toUpperCase();
      return !s?.status || ['NEW', 'SHOWN'].includes(st);
    });
  }, [suggestionsInView]);
  const groupHasDismissableSuggestions = useMemo(() => {
    const opts = Array.isArray(suggestionsInView) ? suggestionsInView : [];
    return opts.some((s) => {
      const st = String(s?.status || '').toUpperCase();
      return !s?.status || ['NEW', 'SHOWN'].includes(st);
    });
  }, [suggestionsInView]);

  const groupAppliedAt = useMemo(() => {
    const opts = Array.isArray(suggestionsInView) ? suggestionsInView : [];
    if (!groupMode || opts.length === 0) return null;
    const allApplied = opts.every((s) => String(s?.status || '').toUpperCase() === 'APPLIED');
    if (!allApplied) return null;
    let pick = null;
    let bestT = null;
    for (const s of opts) {
      if (!s?.applied_at) continue;
      const t = new Date(s.applied_at).getTime();
      if (Number.isNaN(t)) continue;
      if (bestT === null || t > bestT) {
        bestT = t;
        pick = s.applied_at;
      }
    }
    return pick;
  }, [groupMode, suggestionsInView]);

  const getCurrencyCodeForSuggestion = useCallback((suggestion) => {
    const fromSuggestion = normalizeCurrencyCode(
      suggestion?.value_unit ??
      suggestion?.metrics?.currency ??
      suggestion?.currency
    );
    if (fromSuggestion && fromSuggestion !== 'NONE') return fromSuggestion;

    const platform = String(suggestion?.platform || '').toLowerCase();
    if (platform.includes('meta')) {
      return endpointMetaCurrency || activeMetaCurrency || endpointGoogleCurrency || activeGoogleCurrency || 'USD';
    }
    if (platform.includes('google')) {
      return endpointGoogleCurrency || activeGoogleCurrency || endpointMetaCurrency || activeMetaCurrency || 'USD';
    }
    return endpointMetaCurrency || activeMetaCurrency || endpointGoogleCurrency || activeGoogleCurrency || 'USD';
  }, [activeMetaCurrency, activeGoogleCurrency, endpointMetaCurrency, endpointGoogleCurrency]);

  const isSplitTopSellersMergedView = groupMode && isSplitTopSellersWhenRoasVeryHighRule(normalizedRuleKey);
  const isKeywordOrSearchTermMergedView = useMemo(() => {
    if (!groupMode) return false;
    const ruleOrActionMatches = [
      'pause_keyword',
      'enable_keyword',
      'add_keyword',
      'add_negative_keyword',
      'change_keyword_match_type',
      'pause_search_term',
      'exclude_search_term',
    ].includes(String(normalizedRuleKey || '').toLowerCase()) || /keyword|search_term|match/.test(String(normalizedRuleKey || ''));

    if (ruleOrActionMatches) return true;

    return suggestionsInView.some((opt) => {
      const actionType = String(opt?.action_type || '').toLowerCase();
      if (['pause_keyword', 'enable_keyword', 'add_keyword', 'add_negative_keyword', 'change_keyword_match_type', 'pause_search_term', 'exclude_search_term'].includes(actionType)) {
        return true;
      }

      const groups = Array.isArray(opt?.campaign_groups) ? opt.campaign_groups : [];
      return groups.some((cg) => {
        const items = Array.isArray(cg?.items) ? cg.items : [];
        return items.some((item) => /keyword|search[_\s]?term/i.test(String(item?.type || '')) || /keyword|search[_\s]?term/i.test(String(item?.type_label || '')));
      });
    });
  }, [groupMode, normalizedRuleKey, suggestionsInView]);

  const handleGroupDismiss = useCallback(async () => {
    setGroupDiscardSubmitting(true);
    try {
      const ids = Array.from(new Set(
        (suggestionsInView || [])
          .filter((s) => {
            const st = String(s?.status || '').toUpperCase();
            return !s?.status || ['NEW', 'SHOWN'].includes(st);
          })
          .flatMap((opt) => (
            Array.isArray(opt?.source_ids) && opt.source_ids.length > 0 ? opt.source_ids : [opt?.id]
          ))
          .filter((id) => id != null)
      ));
      if (ids.length === 0) return;
      await Promise.all(ids.map((id) => patchRequest(`/api/suggestions/${id}/dismiss`, {})));
      ids.forEach((id) => onRatingSuccess(id, false, { status: 'DISMISSED' }));
      toast.success('Gracias por tu opinión');
    } finally {
      setGroupDiscardSubmitting(false);
    }
  }, [suggestionsInView, onRatingSuccess]);

  const mergedTableRows = useMemo(() => {
    if ((!isSplitTopSellersMergedView && !isKeywordOrSearchTermMergedView) || !suggestionsInView.length) return [];
    // For split_top_sellers we only want products that actually match the
    // suggestion criteria (one per fired suggestion). The BE may emit items
    // beyond the qualifying product in campaign_groups[].items — restrict the
    // merged table to entities that are the primary_entity of some suggestion
    // in view, and dedupe across suggestions.
    const topSellerExtIds = isSplitTopSellersMergedView
      ? new Set(
          suggestionsInView
            .map((s) => s?.primary_entity?.external_id)
            .filter((v) => v != null)
            .map((v) => String(v))
        )
      : null;
    const topSellerInternalIds = isSplitTopSellersMergedView
      ? new Set(
          suggestionsInView
            .map((s) => s?.primary_entity?.id)
            .filter((v) => v != null)
            .map((v) => String(v))
        )
      : null;
    const rows = [];
    const seenTopSellerKeys = isSplitTopSellersMergedView ? new Set() : null;
    for (const opt of suggestionsInView) {
      const groups = Array.isArray(opt.campaign_groups) ? opt.campaign_groups : [];
      for (const cg of groups) {
        const items = Array.isArray(cg.items) ? cg.items : [];
        for (const item of items) {
          if (isSplitTopSellersMergedView) {
            const itemExtId = item?.target_entity?.external_id ?? item?.external_id ?? null;
            const itemId = item?.target_entity?.id ?? item?.id ?? null;
            const matchesByExtId = itemExtId != null && topSellerExtIds.has(String(itemExtId));
            const matchesById = itemId != null && topSellerInternalIds.has(String(itemId));
            if (!matchesByExtId && !matchesById) continue;
            const dedupeKey = matchesByExtId ? `ext:${itemExtId}` : `id:${itemId}`;
            if (seenTopSellerKeys.has(dedupeKey)) continue;
            seenTopSellerKeys.add(dedupeKey);
          }
          rows.push({
            rowId: `${opt?.id ?? 'opt'}-${cg?.campaign_id ?? cg?.campaign_name ?? 'cg'}-${item?.id ?? item?.name ?? 'item'}`,
            entity: item.name ?? '-',
            campaign: cg.campaign_name ?? '-',
            campaignId: cg.campaign_id ?? null,
            metrics: item.metrics ?? {},
            item,
            opt,
          });
        }
      }
    }
    return rows;
  }, [isSplitTopSellersMergedView, isKeywordOrSearchTermMergedView, suggestionsInView]);
  const [selectedMergedRowIds, setSelectedMergedRowIds] = useState(() => new Set());
  const selectedMergedRows = useMemo(
    () => mergedTableRows.filter((row) => selectedMergedRowIds.has(row.rowId)),
    [mergedTableRows, selectedMergedRowIds]
  );
  const selectedMergedSuggestionIds = useMemo(
    () => Array.from(new Set(selectedMergedRows.map((row) => row?.opt?.id).filter(Boolean))),
    [selectedMergedRows]
  );
  const hasSelectedMergedRows = selectedMergedRows.length > 0;
  const allMergedRowsSelected = mergedTableRows.length > 0 && selectedMergedRows.length === mergedTableRows.length;

  useEffect(() => {
    setSelectedMergedRowIds(new Set());
  }, [normalizedRuleKey, currentGroupIndex, suggestionsInView.length]);

  /** Per-row match type (Google ADD_KEYWORD / ADD_NEGATIVE) in merged keyword table */
  const [mergedRowMatchTypeByRowId, setMergedRowMatchTypeByRowId] = useState({});
  const showKeywordMatchTypeColumn = useMemo(() => {
    if (!isKeywordOrSearchTermMergedView || isSplitTopSellersMergedView) return false;
    // Solo mostrar “Concordancia” cuando haya acciones que realmente la necesiten (agregar/cambiar).
    return mergedTableRows.some((row) => {
      const at = String(row?.opt?.action_type || '').trim().toUpperCase();
      return at === 'ADD_KEYWORD' || at === 'ADD_NEGATIVE_KEYWORD' || at === 'CHANGE_KEYWORD_MATCH_TYPE';
    });
  }, [isKeywordOrSearchTermMergedView, isSplitTopSellersMergedView, mergedTableRows]);

  useEffect(() => {
    if (!showKeywordMatchTypeColumn) {
      setMergedRowMatchTypeByRowId({});
      return;
    }
    setMergedRowMatchTypeByRowId((prev) => {
      const next = { ...prev };
      const validIds = new Set(mergedTableRows.map((r) => r.rowId));
      for (const k of Object.keys(next)) {
        if (!validIds.has(k)) delete next[k];
      }
      for (const row of mergedTableRows) {
        if (next[row.rowId] != null) continue;
        const at = String(row.opt?.action_type || '').trim().toUpperCase();
        const d = row.opt?.action_params?.defaults;
        const to = row.opt?.action_params?.to_match_type;
        const raw =
          at === 'CHANGE_KEYWORD_MATCH_TYPE'
            ? (to || 'EXACT').toString().trim().toUpperCase()
            : (d?.match_type || 'BROAD').toString().trim().toUpperCase();
        next[row.rowId] = ['BROAD', 'PHRASE', 'EXACT'].includes(raw) ? raw : 'BROAD';
      }
      return next;
    });
  }, [mergedTableRows, showKeywordMatchTypeColumn]);

  const setMergedRowMatchType = useCallback((rowId, value) => {
    setMergedRowMatchTypeByRowId((prev) => ({ ...prev, [rowId]: value }));
  }, []);

  const toggleMergedRowSelection = useCallback((rowId) => {
    setSelectedMergedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const toggleSelectAllMergedRows = useCallback(() => {
    setSelectedMergedRowIds((prev) => {
      if (mergedTableRows.length === 0) return prev;
      const allSelected = mergedTableRows.every((row) => prev.has(row.rowId));
      if (allSelected) return new Set();
      return new Set(mergedTableRows.map((row) => row.rowId));
    });
  }, [mergedTableRows]);

  const [applyingSuggestionIds, setApplyingSuggestionIds] = useState({});
  const isApplyingSuggestion = useCallback((id) => !!applyingSuggestionIds?.[id], [applyingSuggestionIds]);

  const [valueAfterBySuggestionId, setValueAfterBySuggestionId] = useState({});
  const [keywordMatchTypeBySuggestionId, setKeywordMatchTypeBySuggestionId] = useState({});
  const [isDItemsExpanded, setIsDItemsExpanded] = useState(false);
  const [isEItemsExpanded, setIsEItemsExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // Reset por sugerencia (para que al navegar no quede expandido)
    setIsDItemsExpanded(false);
    setIsEItemsExpanded(false);
  }, [isOpen, currentOptimization?.id]);

  useEffect(() => {
    if (!isOpen) return;
    const candidates = [
      ...(Array.isArray(suggestionsInView) ? suggestionsInView : []),
      currentOptimization,
    ].filter(Boolean);

    setValueAfterBySuggestionId((prev) => {
      const next = { ...(prev || {}) };
      for (const s of candidates) {
        if (!s?.id) continue;
        if (!isBudgetActionType(s)) continue;
        if (next[s.id] != null) continue;
        const initial = (
          s?.value_after ??
          s?.new_daily_budget ??
          s?.daily_budget ??
          null
        );
        next[s.id] = coerceNumberOrEmpty(initial);
      }
      return next;
    });

    setKeywordMatchTypeBySuggestionId((prev) => {
      const next = { ...(prev || {}) };
      for (const s of candidates) {
        if (!s?.id) continue;
        if (!isKeywordMatchTypeSelectableAction(s)) continue;
        if (next[s.id] != null) continue;
        const at = String(s?.action_type || '').trim().toUpperCase();
        const d = s?.action_params?.defaults;
        const to = s?.action_params?.to_match_type;
        const raw =
          at === 'CHANGE_KEYWORD_MATCH_TYPE'
            ? (to || 'EXACT').toString().trim().toUpperCase()
            : (d?.match_type || 'BROAD').toString().trim().toUpperCase();
        next[s.id] = ['BROAD', 'PHRASE', 'EXACT'].includes(raw) ? raw : 'BROAD';
      }
      return next;
    });
  }, [isOpen, suggestionsInView, currentOptimization]);

  const applySuggestion = useCallback(async (suggestion, { execute = true, value_after = undefined, execution_params = undefined } = {}) => {
    const suggestionId = suggestion?.id ?? suggestion;
    console.log('[applySuggestion] called', { suggestionId, execute, value_after, execution_params, action_type: suggestion?.action_type });
    if (!suggestionId) return null;
    if (isApplyingSuggestion(suggestionId)) return null;
    setApplyingSuggestionIds((prev) => ({ ...(prev || {}), [suggestionId]: true }));
    try {
      console.log('[applySuggestion] PATCH /api/suggestions/' + suggestionId + '/apply', { execute, value_after, execution_params });
      const body = { execute };
      if (value_after !== undefined) body.value_after = value_after;
      if (execution_params !== undefined) body.execution_params = execution_params;
      const response = await patchRequest(`/api/suggestions/${suggestionId}/apply`, body);
      console.log('[applySuggestion] response', response?.data);
      const data = response?.data;
      const execution = data?.execution;
      if (execution?.attempted && !execution?.executed) {
        const errorMsg = execution?.error || 'No se pudo ejecutar el cambio en la plataforma.';
        toast.error(errorMsg);
        if (data?.status === 'FAILED') {
          onApplySuccess?.(suggestionId, 'FAILED');
        }
      } else if (execution?.executed) {
        toast.success('Cambio ejecutado en la plataforma.');
        onApplySuccess?.(suggestionId, 'APPLIED');
      } else if (data?.status === 'APPLIED') {
        onApplySuccess?.(suggestionId, 'APPLIED');
      }
      return data ?? null;
    } catch (err) {
      console.warn('No se pudo aplicar la sugerencia', suggestionId, err);
      toast.error('No se pudo aplicar la sugerencia. Intentá de nuevo.');
      return null;
    } finally {
      setApplyingSuggestionIds((prev) => {
        const next = { ...(prev || {}) };
        delete next[suggestionId];
        return next;
      });
    }
  }, [isApplyingSuggestion, onApplySuccess]);

  const applyAndOpenDestination = useCallback(async (opt, params = {}) => {
    console.log('[applyAndOpenDestination] called', { id: opt?.id, action_type: opt?.action_type, isExecutable: isExecutableActionType(opt?.action_type) });
    if (!opt?.id) return;
    if (isExecutableActionType(opt?.action_type)) {
      const suggestionId = opt?.id;
      const rawBudget = valueAfterBySuggestionId?.[suggestionId];
      const parsedBudget = rawBudget !== undefined && rawBudget !== null && rawBudget !== '' ? Number(rawBudget) : undefined;
      const value_after =
        isBudgetActionType(opt) && Number.isFinite(parsedBudget) ? parsedBudget : undefined;

      let execution_params = buildDArchetypeExecutionParams(opt);
      if (isKeywordMatchTypeSelectableAction(opt)) {
        const mt = keywordMatchTypeBySuggestionId?.[suggestionId] || 'BROAD';
        execution_params = { ...(execution_params || {}), match_type: mt };
      }

      const result = await applySuggestion(opt, { execute: true, value_after, execution_params });
      if (!result?.execution?.executed) {
        openSuggestionDestination(opt, params);
      }
      return;
    }
    await applySuggestion(opt);
    openSuggestionDestination(opt, params);
  }, [applySuggestion, openSuggestionDestination, valueAfterBySuggestionId, keywordMatchTypeBySuggestionId]);

  const retryFailedSuggestion = useCallback(async (opt) => {
    if (!opt?.id) return;
    if (isApplyingSuggestion(opt.id)) return;
    const suggestionId = opt.id;
    const rawBudget = valueAfterBySuggestionId?.[suggestionId];
    const parsedBudget = rawBudget !== undefined && rawBudget !== null && rawBudget !== '' ? Number(rawBudget) : undefined;
    const value_after = isBudgetActionType(opt) && Number.isFinite(parsedBudget) ? parsedBudget : undefined;
    let execution_params = buildDArchetypeExecutionParams(opt);
    if (isKeywordMatchTypeSelectableAction(opt)) {
      const mt = keywordMatchTypeBySuggestionId?.[suggestionId] || 'BROAD';
      execution_params = { ...(execution_params || {}), match_type: mt };
    }
    await applySuggestion(opt, { execute: true, value_after, execution_params });
  }, [isApplyingSuggestion, applySuggestion, valueAfterBySuggestionId, keywordMatchTypeBySuggestionId]);

  const renderFailedCta = (opt) => (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center rounded-full border border-rose-400/35 bg-rose-500/15 px-2 py-0.5 text-[10px] font-semibold text-rose-100 whitespace-nowrap">
        Falló
      </span>
      <button
        type="button"
        disabled={isApplyingSuggestion(opt?.id)}
        onClick={() => retryFailedSuggestion(opt)}
        className="shrink-0 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border border-rose-400/35 bg-rose-500/15 text-rose-100 disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 transition"
        aria-label="Reintentar"
      >
        {isApplyingSuggestion(opt?.id) ? (
          <><Loader className="w-3 h-3 shrink-0 animate-spin inline mr-1" aria-hidden />Reintentando…</>
        ) : 'Reintentar'}
      </button>
    </div>
  );

  const isApplyingMergedSelection = selectedMergedSuggestionIds.some((id) => isApplyingSuggestion(id));
  const applySelectedMergedRows = useCallback(async () => {
    console.log('[applySelectedMergedRows] called', { hasSelectedMergedRows, count: selectedMergedRows?.length });
    if (!hasSelectedMergedRows) return;
    const selectedOpts = selectedMergedRows.map((row) => row.opt).filter(Boolean);
    console.log('[applySelectedMergedRows] opts', selectedOpts.map(o => ({ id: o?.id, action_type: o?.action_type, isExec: isExecutableActionType(o?.action_type) })));

    const matchTypeBySuggestionId = {};
    for (const row of selectedMergedRows) {
      const o = row.opt;
      if (!o || !isExecutableActionType(o.action_type)) continue;
      if (!isGoogleAdsPlatformUi(o.platform)) continue;
      const at = String(o.action_type || '').trim().toUpperCase();
      if (at !== 'ADD_KEYWORD' && at !== 'ADD_NEGATIVE_KEYWORD' && at !== 'CHANGE_KEYWORD_MATCH_TYPE') continue;
      const mt = mergedRowMatchTypeByRowId[row.rowId] || 'BROAD';
      matchTypeBySuggestionId[o.id] = mt;
    }

    /** Pause/enable: selected rows → criterion + ad_group for batch execution */
    const keywordStatusTargetsBySuggestionId = {};
    for (const row of selectedMergedRows) {
      const o = row.opt;
      if (!o || !isExecutableActionType(o.action_type)) continue;
      const at = String(o.action_type || '').trim().toUpperCase();
      if (at !== 'PAUSE_KEYWORD' && at !== 'ENABLE_KEYWORD') continue;
      const sid = o.id;
      const crit = row.item?.criterion_external_id ?? row.item?.id;
      const ag = row.item?.ad_group_id;
      if (sid == null || crit == null || ag == null) continue;
      const pair = { criterion_id: String(crit), ad_group_id: String(ag) };
      if (!keywordStatusTargetsBySuggestionId[sid]) {
        keywordStatusTargetsBySuggestionId[sid] = [];
      }
      const list = keywordStatusTargetsBySuggestionId[sid];
      if (!list.some((p) => p.criterion_id === pair.criterion_id && p.ad_group_id === pair.ad_group_id)) {
        list.push(pair);
      }
    }

    const seenExec = new Set();
    const executableOpts = [];
    for (const row of selectedMergedRows) {
      const o = row.opt;
      if (!o || !isExecutableActionType(o.action_type)) continue;
      if (seenExec.has(o.id)) continue;
      seenExec.add(o.id);
      executableOpts.push(o);
    }

    const nonExecutableOpts = selectedOpts.filter((opt) => !isExecutableActionType(opt?.action_type));

    if (executableOpts.length > 0) {
      if (nonExecutableOpts.length > 0) {
        await Promise.all(nonExecutableOpts.map((opt) => applySuggestion(opt)));
      }
      await Promise.all(
        executableOpts.map((opt) => {
          const suggestionId = opt?.id;
          const actionType = String(opt?.action_type || '').trim().toUpperCase();
          const isMatchTypeSelectable = isGoogleAdsPlatformUi(opt?.platform) && (actionType === 'ADD_KEYWORD' || actionType === 'ADD_NEGATIVE_KEYWORD' || actionType === 'CHANGE_KEYWORD_MATCH_TYPE');
          const mt =
            isMatchTypeSelectable && suggestionId != null && matchTypeBySuggestionId[suggestionId] != null
              ? matchTypeBySuggestionId[suggestionId]
              : undefined;
          const kt =
            suggestionId != null && keywordStatusTargetsBySuggestionId
              ? keywordStatusTargetsBySuggestionId[suggestionId]
              : undefined;
          let execution_params = buildDArchetypeExecutionParams(opt);
          if (isMatchTypeSelectable && mt) {
            execution_params = { ...(execution_params || {}), match_type: mt };
          }
          if (
            (actionType === 'PAUSE_KEYWORD' || actionType === 'ENABLE_KEYWORD') &&
            Array.isArray(kt) &&
            kt.length > 0
          ) {
            execution_params = { ...(execution_params || {}), keyword_targets: kt };
          }
          const rawBudget = suggestionId != null ? valueAfterBySuggestionId?.[suggestionId] : undefined;
          const parsedBudget = rawBudget !== undefined && rawBudget !== null && rawBudget !== '' ? Number(rawBudget) : undefined;
          const value_after =
            isBudgetActionType(opt) && Number.isFinite(parsedBudget) ? parsedBudget : undefined;
          return applySuggestion(opt, { execute: true, value_after, execution_params });
        })
      );
      return;
    }

    await Promise.all(selectedMergedSuggestionIds.map((id) => applySuggestion(id)));
    const firstRow = selectedMergedRows[0];
    if (!firstRow?.opt) return;
    openSuggestionDestination(firstRow.opt, {
      entityName: firstRow.entity,
      campaignId: firstRow.campaignId,
    });
  }, [hasSelectedMergedRows, selectedMergedSuggestionIds, selectedMergedRows, mergedRowMatchTypeByRowId, applySuggestion, openSuggestionDestination, valueAfterBySuggestionId]);

  const [showAllDiagByRule, setShowAllDiagByRule] = useState({});
  const [viewedDiagIds, setViewedDiagIds] = useState({});
  const [pendingHReview, setPendingHReview] = useState(null);
  const [subItemSelectionBySuggestionId, setSubItemSelectionBySuggestionId] = useState({});

  const getSubItemSelection = useCallback((suggestionId, subItems) => {
    const stored = subItemSelectionBySuggestionId[suggestionId];
    if (Array.isArray(stored)) return stored;
    if (!Array.isArray(subItems)) return [];
    return subItems
      .filter((it) => it?.id != null && it?.selected_by_default !== false)
      .map((it) => String(it.id));
  }, [subItemSelectionBySuggestionId]);

  const toggleSubItemSelection = useCallback((suggestionId, subItems, subItemId) => {
    if (suggestionId == null || subItemId == null) return;
    setSubItemSelectionBySuggestionId((prev) => {
      const stored = Array.isArray(prev?.[suggestionId])
        ? prev[suggestionId]
        : (Array.isArray(subItems)
          ? subItems
            .filter((it) => it?.id != null && it?.selected_by_default !== false)
            .map((it) => String(it.id))
          : []);
      const id = String(subItemId);
      const next = stored.includes(id)
        ? stored.filter((x) => x !== id)
        : [...stored, id];
      return { ...(prev || {}), [suggestionId]: next };
    });
  }, []);

  const confirmPendingHReview = useCallback(async () => {
    if (!pendingHReview) return;
    const { opt, destinationParams } = pendingHReview;
    setPendingHReview(null);
    await applySuggestion(opt, {
      execute: true,
      execution_params: { confirm_review_done: true },
    });
    openSuggestionDestination(opt, destinationParams);
  }, [pendingHReview, applySuggestion, openSuggestionDestination]);

  const isDiagnosticMetaRule = groupMode && [
    'diag_reduce_budget_saturation',
    'diag_scale_budget',
    'diag_rotate_creative',
    'diag_increase_budget_underfunded',
  ].includes(normalizedRuleKey);

  const isDiagRotateCreative = normalizedRuleKey === 'diag_rotate_creative';
  const isSwitchToAdvantagePlusRule = typeof normalizedRuleKey === 'string' && normalizedRuleKey.includes('switch_to_advantage_plus');
  const isLandingPageRule = typeof normalizedRuleKey === 'string' && (
    normalizedRuleKey.includes('landing_page') ||
    normalizedRuleKey.includes('lp_speed') ||
    normalizedRuleKey.includes('lp_relevance') ||
    normalizedRuleKey.includes('improve_lp') ||
    normalizedRuleKey.includes('optimize_lp')
  );
  const isMetaReviewOnlyGroupFooter = isDiagRotateCreative || isSwitchToAdvantagePlusRule || isLandingPageRule;

  const isMetaReviewOnlyActionType = useCallback((actionType) => {
    const at = String(actionType || '').trim().toUpperCase();
    return (
      at === 'REFRESH_CREATIVE' ||
      at === 'SWITCH_TO_ADVANTAGE_PLUS' ||
      at === 'EXPAND_AUDIENCE_BROAD' ||
      at === 'CONSOLIDATE_ADSETS' ||
      at === 'ENABLE_ADVANTAGE_PLUS_PLACEMENTS' ||
      at === 'CREATE_REELS_CREATIVE' ||
      at === 'OPTIMIZE_LANDING_PAGE_SPEED' ||
      at === 'IMPROVE_LANDING_PAGE_RELEVANCE'
    );
  }, []);

  const isGoogleReviewOnlyActionType = useCallback((actionType) => {
    const at = String(actionType || '').trim().toUpperCase();
    return at === 'SPLIT_TOP_SELLERS';
  }, []);

  const diagConfig = useMemo(() => {
    const base = {
      showBudget: true,
      budgetSuggestedClass: 'text-amber-400',
      actionBadgeClass: 'bg-amber-500/20 text-amber-100 border-amber-400/30',
      actionLabel: 'Acción',
      title: '',
      description: '',
      ctaLabel: 'Aplicar',
      footerLabel: (n) => `Aplicar todas (${n})`,
      onCta: ({ adExternalId, entityName, dateStart, dateEnd }) =>
        openAdsManager({ selectedAdIds: adExternalId, entityName, dateStart, dateEnd }),
      onFooter: ({ adExternalIds, entityName, dateStart, dateEnd }) =>
        openAdsManager({ selectedAdIds: adExternalIds, entityName, dateStart, dateEnd }),
      metrics: [],
    };

    if (normalizedRuleKey === 'diag_reduce_budget_saturation') {
      return {
        ...base,
        actionLabel: 'Reducir presupuesto',
        title: 'Estos anuncios están perdiendo tracción',
        description: 'Retorno y clics en caída sostenida. Recomendamos bajar el presupuesto un 20%.',
        budgetSuggestedClass: 'text-amber-400',
        actionBadgeClass: 'bg-amber-500/20 text-amber-100 border-amber-400/30',
        metrics: [
          { label: 'Gasto', field: 'spend_7d', prev: 'spend_prev_7d', format: 'currency', upIsGood: false },
          { label: 'ROAS', field: 'roas_7d', prev: 'roas_prev_7d', format: 'roas', upIsGood: true },
          { label: 'Conv.', field: 'conversions_7d', prev: 'conversions_prev_7d', format: 'integer', upIsGood: true },
        ],
      };
    }
    if (normalizedRuleKey === 'diag_scale_budget') {
      return {
        ...base,
        actionLabel: 'Aumentar presupuesto',
        title: 'Estos anuncios están rindiendo bien',
        description: 'ROAS sostenido por encima del objetivo y sin señales de fatiga. Recomendamos subir el presupuesto un 20% para escalar.',
        budgetSuggestedClass: 'text-green-300',
        actionBadgeClass: 'bg-green-500/20 text-green-100 border-green-400/30',
        metrics: [
          { label: 'ROAS', field: 'roas_7d', prev: 'roas_prev_7d', format: 'roas', upIsGood: true },
          { label: 'Conv.', field: 'conversions_7d', prev: 'conversions_prev_7d', format: 'integer', upIsGood: true },
          { label: 'Gasto', field: 'spend_7d', prev: 'spend_prev_7d', format: 'currency', upIsGood: true },
        ],
      };
    }
    if (normalizedRuleKey === 'diag_increase_budget_underfunded') {
      return {
        ...base,
        actionLabel: 'Aumentar presupuesto',
        title: 'Estos anuncios rinden bien pero gastan poco',
        description: 'ROAS por encima del objetivo con gasto bajo. Subir el presupuesto un 20% puede aprovechar mejor su potencial.',
        budgetSuggestedClass: 'text-green-300',
        actionBadgeClass: 'bg-green-500/20 text-green-100 border-green-400/30',
        metrics: [
          { label: 'ROAS', field: 'roas_7d', prev: 'roas_prev_7d', format: 'roas', upIsGood: true },
          { label: 'Gasto', field: 'spend_7d', prev: 'spend_prev_7d', format: 'currency', upIsGood: true },
          { label: 'Conv.', field: 'conversions_7d', prev: 'conversions_prev_7d', format: 'integer', upIsGood: true },
        ],
      };
    }
    if (normalizedRuleKey === 'diag_rotate_creative') {
      return {
        ...base,
        actionLabel: 'Renovar creativo',
        title: 'Estos creativos ya agotaron su audiencia',
        description: 'Los clics caen mientras la frecuencia sube y el alcance se estanca. Es momento de rotar con creativos nuevos.',
        showBudget: false,
        actionBadgeClass: 'bg-blue-500/20 text-blue-100 border-blue-400/30',
        ctaLabel: (opt) => (isGoogleAdsPlatformUi(opt?.platform) ? 'Ver en Google' : 'Ver en Meta'),
        footerLabel: (n) => `Marcar como vistas (${n})`,
        onCta: ({ opt, adExternalId, entityName, dateStart, dateEnd, suggestionId, campaignId }) => {
          openSuggestionDestination(opt, { adExternalId, entityName, dateStart, dateEnd, campaignId });
          setViewedDiagIds((prev) => ({ ...(prev || {}), [suggestionId]: true }));
        },
        onFooter: ({ adExternalIds, suggestionIds, entityName, dateStart, dateEnd }) => {
          openAdsManager({ selectedAdIds: adExternalIds, entityName, dateStart, dateEnd });
          setViewedDiagIds((prev) => {
            const next = { ...(prev || {}) };
            (suggestionIds || []).forEach((id) => { next[id] = true; });
            return next;
          });
        },
        metrics: [
          { label: 'CTR', field: 'ctr_7d', prev: 'ctr_prev_7d', format: 'percentage', upIsGood: true },
          { label: 'Frecuencia', field: 'frequency_7d', prev: 'frequency_prev_7d', format: 'decimal', upIsGood: false },
          { label: 'Alcance', field: 'reach_7d', prev: 'reach_prev_7d', format: 'integer', upIsGood: true },
        ],
      };
    }
    return null;
  }, [normalizedRuleKey, openAdsManager, openSuggestionDestination]);

  const getSortedDiagSuggestions = useCallback(() => {
    const arr = [...suggestionsInView];
    arr.sort((a, b) => {
      const sa = a?.metrics_extended?.spend_7d ?? a?.metrics_extended?.spend;
      const sb = b?.metrics_extended?.spend_7d ?? b?.metrics_extended?.spend;
      const na = sa == null ? -Infinity : Number(sa);
      const nb = sb == null ? -Infinity : Number(sb);
      return (Number.isFinite(nb) ? nb : -Infinity) - (Number.isFinite(na) ? na : -Infinity);
    });
    return arr;
  }, [suggestionsInView]);

  const referenceSuggestion = useMemo(() => {
    const candidates = [
      ...(Array.isArray(suggestionsInView) ? suggestionsInView : []),
      currentOptimization,
    ].filter(Boolean);

    const withAnalysisWindow = candidates.find((s) => s?.evidence?.analysis_window);
    if (withAnalysisWindow) return withAnalysisWindow;
    return candidates[0] || null;
  }, [suggestionsInView, currentOptimization]);

  const temporalContext = useMemo(() => {
    const createdAtDate = parseCreatedAtDateOnly(referenceSuggestion?.created_at);
    const analysisWindow = referenceSuggestion?.evidence?.analysis_window || {};

    // For Meta diagnostic rules (D1-D4), use suggestion creation date as temporal anchor.
    if (isDiagnosticMetaRule) {
      if (!createdAtDate) return null;
      const recentEnd = addDays(createdAtDate, -1);
      const recentStart = addDays(recentEnd, -6);
      const previousEnd = addDays(recentStart, -1);
      const previousStart = addDays(previousEnd, -6);

      return {
        analysisRange: formatAnalyzedRangeEs(recentStart, recentEnd),
        windowDaysLabel: '7 días',
        recentRange: formatRangeEs(recentStart, recentEnd),
        previousRange: formatRangeEs(previousStart, previousEnd),
        recentStartDate: recentStart,
        recentEndDate: recentEnd,
      };
    }

    const endDate = parseISODateOnly(analysisWindow.end_date) || createdAtDate;
    if (!endDate) return null;
    const startDate =
      parseISODateOnly(analysisWindow.start_date) ||
      (analysisWindow.window_days ? addDays(endDate, -(Number(analysisWindow.window_days) - 1)) : null);
    const recentEnd = endDate;
    const recentStart = addDays(recentEnd, -6);
    const previousEnd = addDays(recentStart, -1);
    const previousStart = addDays(previousEnd, -6);
    const windowDays = Number(analysisWindow.window_days);
    const windowDaysLabel = Number.isFinite(windowDays) && windowDays > 0 ? `${windowDays} días` : null;

    return {
      analysisRange: startDate ? formatAnalyzedRangeEs(startDate, endDate) : null,
      windowDaysLabel,
      analysisWindowName: analysisWindow?.window_name ? String(analysisWindow.window_name) : null,
      analysisWindowPlatform: analysisWindow?.platform ? String(analysisWindow.platform).toUpperCase() : null,
      recentRange: formatRangeEs(recentStart, recentEnd),
      previousRange: formatRangeEs(previousStart, previousEnd),
      recentStartDate: recentStart,
      recentEndDate: recentEnd,
    };
  }, [referenceSuggestion, isDiagnosticMetaRule]);

  const headerActionLabel = isDiagnosticMetaRule
    ? (diagConfig?.actionLabel || currentOptimization?.action || 'Optimización')
    : (currentOptimization?.action || 'Optimización');

  if (!isOpen || !currentOptimization) return null;

  // Group mode: nav between groups. Single mode: nav between suggestions.
  const hasPreviousGroup = groupMode ? currentGroupIndex > 0 : currentIndex > 0;
  const hasNextGroup = groupMode ? currentGroupIndex < allGroups.length - 1 : currentIndex < allOptimizations.length - 1;
  const navLabel = groupMode
    ? `${currentGroupIndex + 1} / ${allGroups.length}`
    : `${currentIndex + 1} / ${allOptimizations.length}`;
  const handlePrevious = () => {
    if (groupMode && hasPreviousGroup && onNavigateGroup) onNavigateGroup(currentGroupIndex - 1);
    else if (!groupMode && hasPreviousGroup && onNavigate) onNavigate(currentIndex - 1);
  };
  const handleNext = () => {
    if (groupMode && hasNextGroup && onNavigateGroup) onNavigateGroup(currentGroupIndex + 1);
    else if (!groupMode && hasNextGroup && onNavigate) onNavigate(currentIndex + 1);
  };
  const showNav = groupMode ? allGroups.length > 1 : allOptimizations.length > 1;

  const toFiniteNumberOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const resolveRoasTarget = (item, opt) => {
    const target = item?.metrics_extended?.target_roas
      ?? opt?.metrics_extended?.target_roas
      ?? opt?.target_roas
      ?? null;
    return toFiniteNumberOrNull(target);
  };

  const resolveCpaTarget = (item, opt) => {
    const target = item?.metrics_extended?.target_cpa
      ?? opt?.metrics_extended?.target_cpa
      ?? opt?.target_cpa
      ?? null;
    return toFiniteNumberOrNull(target);
  };

  /** ROAS cell class: red when below target, green when at/above target, neutral when no target or no value. */
  const getRoasCellClassName = (item, opt) => {
    const target = resolveRoasTarget(item, opt);
    const value = toFiniteNumberOrNull(item?.metrics?.roas);
    if (target == null || value == null) return 'text-white/60';
    if (value < target) return 'text-red-300';
    return 'text-green-300';
  };

  /** CPA cell class: red when above target, green when at/below target, neutral when no target or no value. */
  const getCpaCellClassName = (item, opt) => {
    const target = resolveCpaTarget(item, opt);
    const value = toFiniteNumberOrNull(item?.metrics?.cpa);
    if (target == null || value == null) return 'text-white/60';
    if (value > target) return 'text-red-300';
    return 'text-green-300';
  };

  const getRateHandler = (opt) => {
    const ids =
      Array.isArray(opt?.source_ids) && opt.source_ids.length > 0 ? opt.source_ids : [opt.id];
    return async (_itemId, rating, _userFeedback) => {
      if (rating === 'negative') {
        await Promise.all(ids.map((id) => patchRequest(`/api/suggestions/${id}/dismiss`, {})));
        ids.forEach((id) => onRatingSuccess(id, false, { status: 'DISMISSED' }));
        return;
      }
      const is_liked = rating === 'positive' ? true : null;
      const body = { is_liked };
      await Promise.all(ids.map((id) => patchRequest(`/api/suggestions/${id}/like`, body)));
      ids.forEach((id) => onRatingSuccess(id, is_liked));
    };
  };

  // Delta helpers: 7d vs prev_7d with contextual coloring (see brief)
  const getDeltaPct = (current7d, prev7d) => {
    if (prev7d === null || prev7d === undefined) return null;
    const c = Number(current7d);
    const p = Number(prev7d);
    if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return null;
    return ((c - p) / p) * 100;
  };

  const getDeltaColor = (deltaPct, upIsGood) => {
    if (deltaPct === null) return 'gray';
    const abs = Math.abs(deltaPct);
    if (abs < 5) return 'gray';
    const isUp = deltaPct > 0;
    if (upIsGood === null) {
      return isUp ? 'green' : 'red';
    }
    const isGoodDirection = (isUp && upIsGood) || (!isUp && !upIsGood);
    return isGoodDirection ? 'green' : 'red';
  };

  const formatDeltaMetricValue = (format, value, currencyCode = 'USD') => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    if (format === 'currency') return formatCurrencyNoDecimals(value, currencyCode);
    if (format === 'roas') return renderRoas(value);
    if (format === 'percentage') return renderCtr(value);
    if (format === 'integer') return `${Math.round(Number(value))}`;
    if (format === 'decimal') return renderFrequency(value);
    return String(value);
  };

  const buildDeltaTooltip = ({ metricLabel, format, current, prev, deltaPct, opt, temporalContext, currencyCode = 'USD' }) => {
    if (!temporalContext) return null;
    const currentText = formatDeltaMetricValue(format, current, currencyCode);
    const prevText = formatDeltaMetricValue(format, prev, currencyCode);
    const line1 = `${metricLabel}: ${currentText} (${temporalContext.recentRange}) vs ${prevText} (${temporalContext.previousRange})`;

    const conversions = Number(opt?.metrics_extended?.conversions_7d ?? opt?.metrics_extended?.conversions);
    const roas = Number(opt?.metrics_extended?.roas_7d ?? opt?.metrics_extended?.roas);
    const targetRoas = Number(opt?.metrics_extended?.target_roas ?? opt?.target_roas);
    const interpretations = [];
    if (Number.isFinite(conversions) && conversions === 0) {
      interpretations.push('sin generar conversiones');
    } else if (
      Number.isFinite(conversions) &&
      conversions > 0 &&
      Number.isFinite(roas) &&
      Number.isFinite(targetRoas) &&
      roas < targetRoas
    ) {
      interpretations.push('con ROAS por debajo del objetivo');
    }

    const roundedDelta = Number.isFinite(deltaPct) ? Math.round(Math.abs(deltaPct)) : null;
    const directionWord = Number.isFinite(deltaPct)
      ? (deltaPct > 0 ? 'más' : deltaPct < 0 ? 'menos' : 'sin cambios')
      : null;
    const changePrefix = roundedDelta != null && directionWord
      ? `${metricLabel} ${roundedDelta}% ${directionWord} que la semana anterior`
      : 'comparación respecto a la semana anterior';
    const line2 = interpretations.length > 0
      ? `${changePrefix}, ${interpretations.join(', ')}`
      : changePrefix;

    return `${line1}\n${line2}`;
  };

  const renderDeltaBadge = (deltaPct, upIsGood, tooltipText = null) => {
    if (deltaPct === null) return null;
    const abs = Math.abs(deltaPct);
    if (abs < 5) {
      return (
        <span className="ml-1 text-[11px] font-semibold tabular-nums text-white/50" title={tooltipText || undefined}>
          —
        </span>
      );
    }
    const rounded = Math.round(deltaPct);
    const isUp = rounded > 0;
    const isDown = rounded < 0;
    const symbol = isUp ? '▲' : isDown ? '▼' : '=';
    const text = `${isUp ? '+' : ''}${rounded}%`;
    const color = getDeltaColor(deltaPct, upIsGood);
    const classByColor = color === 'green'
      ? 'text-green-300'
      : color === 'red'
        ? 'text-red-300'
        : color === 'amber'
          ? 'text-amber-300'
          : 'text-white/50';
    return (
      <span className={`ml-1 text-[11px] font-semibold tabular-nums ${classByColor}`} title={tooltipText || undefined}>
        {symbol}{text}
      </span>
    );
  };

  // (slopes/reach_growth) no longer used for diagnostic UI; kept out intentionally.

  const renderRoas = (value) => {
    if (value === null || value === undefined) return '-';
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return `${n.toFixed(1)}x`;
  };

  const renderCtr = (value) => {
    if (value === null || value === undefined) return '-';
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return `${n.toFixed(1)}%`;
  };

  const renderFrequency = (value) => {
    if (value === null || value === undefined) return '-';
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return n.toFixed(1);
  };

  return (
    <>
    <div className="fixed inset-0 modal-safe-area z-[10100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 border border-white/30 rounded-2xl shadow-2xl max-w-7xl w-full h-[90vh] overflow-hidden">
        <div className="relative z-10 p-6 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="relative flex items-center mb-4 min-h-[34px]">
                {(() => {
                  const raw = typeof currentOptimization?.description === 'string' ? currentOptimization.description.trim() : '';
                  const chipText = raw || headerActionLabel;
                  return (
                    <div className="absolute left-1/2 -translate-x-1/2 w-[70%]">
                      <span
                        className="inline-flex w-full justify-center px-3 py-1 rounded-full text-sm font-semibold border border-amber-400/40 bg-amber-500/20 text-amber-100 text-center whitespace-normal leading-snug"
                        title={raw || undefined}
                      >
                        {chipText}
                      </span>
                    </div>
                  );
                })()}
                {showNav ? (
                  <div className="ml-auto flex items-center space-x-2 shrink-0" role="navigation" aria-label={groupMode ? 'Grupos de sugerencias' : 'Sugerencias'}>
                    <button
                      type="button"
                      onClick={handlePrevious}
                      disabled={!hasPreviousGroup}
                      className={`p-1.5 rounded-full transition-all duration-300 ${
                        hasPreviousGroup
                          ? 'text-white/70 hover:text-white hover:bg-white/10 active:bg-white/20'
                          : 'text-white/20 cursor-not-allowed'
                      }`}
                      title={groupMode ? 'Grupo anterior' : 'Sugerencia anterior'}
                      aria-label={groupMode ? 'Grupo anterior' : 'Sugerencia anterior'}
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <span className="text-xs text-white/60 px-2 min-w-[2.5rem] text-center font-medium">
                      {navLabel}
                    </span>
                    <button
                      type="button"
                      onClick={handleNext}
                      disabled={!hasNextGroup}
                      className={`p-1.5 rounded-full transition-all duration-300 ${
                        hasNextGroup
                          ? 'text-white/70 hover:text-white hover:bg-white/10 active:bg-white/20'
                          : 'text-white/20 cursor-not-allowed'
                      }`}
                      title={groupMode ? 'Siguiente grupo' : 'Siguiente sugerencia'}
                      aria-label={groupMode ? 'Siguiente grupo' : 'Siguiente sugerencia'}
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors shrink-0"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {isDiagnosticMetaRule && temporalContext && (
            <div
              className="mb-4 rounded-xl px-4 py-3 border border-cyan-400/20 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-indigo-500/10 backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
              role="region"
              aria-label="Contexto temporal del análisis"
            >
              <div className="flex items-start gap-2.5 text-xs leading-relaxed">
                <Calendar className="w-4 h-4 mt-0.5 shrink-0 text-cyan-200/90" aria-hidden />
                <div className="min-w-0 text-white/85">
                  {temporalContext.analysisRange && (
                    <div className="text-white/80">
                      <span className="text-white/65">Período analizado:</span>{' '}
                      <span className="font-semibold text-white">{temporalContext.analysisRange}</span>
                      {temporalContext.windowDaysLabel ? ` (${temporalContext.windowDaysLabel})` : null}
                    </div>
                  )}
                  <div className="mt-0.5">
                    <span className="text-white/65">Comparación:</span>{' '}
                    <span className="font-semibold text-white">{temporalContext.previousRange}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isDiagnosticMetaRule && temporalContext?.analysisRange && (
            <div
              className="mb-4 rounded-xl px-4 py-3 border border-cyan-400/20 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-indigo-500/10 backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
              role="region"
              aria-label="Período utilizado para evaluar la sugerencia"
            >
              <div className="flex items-start gap-2.5 text-xs leading-relaxed">
                <Calendar className="w-4 h-4 mt-0.5 shrink-0 text-cyan-200/90" aria-hidden />
                <div className="min-w-0 text-white/85">
                  <div className="text-white/80">
                    <span className="text-white/65">Período analizado:</span>{' '}
                    <span className="font-semibold text-white">{temporalContext.analysisRange}</span>
                    {temporalContext.windowDaysLabel ? ` (${temporalContext.windowDaysLabel})` : null}
                  </div>
                  <div className="mt-0.5">
                    <span className="text-white/65">Comparación:</span>{' '}
                    <span className="font-semibold text-white">{temporalContext.previousRange}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto pr-1">
            {/* Caja con user_text (igual para todas las sugerencias del grupo) */}
            {!isDiagnosticMetaRule &&
              !['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].includes(String(currentOptimization?.archetype || '').toUpperCase()) &&
              currentOptimization?.user_text && (
              <div
                className="mb-6 p-0"
                role="region"
                aria-label="Detalle de la sugerencia"
              >
                <div
                  className={DESCRIPTION_P_CLASS}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeOptimizationDescription(currentOptimization.user_text, {
                      fallbackText: '',
                      pClassName: DESCRIPTION_P_CLASS,
                    })
                  }}
                />
              </div>
            )}

          {suggestionsInView.map((opt, suggIdx) => {
            const currentOptimization = opt;
            return (
              <React.Fragment key={opt?.id ?? `sugg-${suggIdx}`}>
                {suggIdx > 0 ? (
                  <div className="my-8 border-t border-white/10" aria-hidden />
                ) : null}
          {String(currentOptimization?.archetype || '').toUpperCase() === 'A' && (() => {
            const visibleItems = resolveVisibleItems(currentOptimization);
            const intent = currentOptimization?.intent;
            const currency =
              currentOptimization?.metrics_extended?.currency ||
              getCurrencyCodeForSuggestion(currentOptimization) ||
              'USD';
            const criteria = Array.isArray(currentOptimization?.criteria) ? currentOptimization.criteria : [];
            const metricKeysToShow = resolveMetricKeysToShow(currentOptimization, criteria);

            const tone = getIntentTone(intent);
            const actionType = String(currentOptimization?.action_type || '').trim().toUpperCase();
            const isMetaReviewOnly = isMetaReviewOnlyActionType(actionType);
            const isGoogleReviewOnly = isGoogleReviewOnlyActionType(actionType);
            const isReviewOnly = isMetaReviewOnly || isGoogleReviewOnly;
            const statusUpper = String(currentOptimization?.status || '').toUpperCase();
            const isFailed = statusUpper === 'FAILED';
            const canShowApplyCta = !currentOptimization?.status || ['NEW', 'SHOWN'].includes(statusUpper);
            const isAlreadyApplied = statusUpper === 'APPLIED';
            const canTriggerAction = isReviewOnly || canShowApplyCta || isAlreadyApplied;
            const actionLabelForA = currentOptimization?.action_label || (isMetaReviewOnly ? 'Ver en Meta' : isGoogleReviewOnly ? 'Ver en Google' : 'Ver en Meta');

            return (
              <div className="mb-6">
                <div className="mb-4 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                  <div className="px-4 py-3 flex items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <CreativePreview
                        entity={currentOptimization?.primary_entity || visibleItems?.[0]?.target_entity || null}
                        subtitle={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                        size="lg"
                      />
                    </div>
                    {isFailed ? renderFailedCta(currentOptimization) : currentOptimization?.action_label ? (
                      <button
                        type="button"
                        disabled={!canTriggerAction || isAlreadyApplied || isApplyingSuggestion(currentOptimization?.id)}
                        onClick={() => {
                          if (!currentOptimization) return;
                          if (!canTriggerAction) return;
                          if (isAlreadyApplied) return;
                          // Arquetipo A (iterate winner): no hay nada que aplicar; siempre es navegación a la entidad.
                          openSuggestionDestination(currentOptimization, {
                            entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                            campaignId: currentOptimization?.campaign_groups?.[0]?.campaign_id ?? null,
                          });
                        }}
                        className="shrink-0 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border border-white/10 disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 transition"
                        style={{ background: tone.pillBg, color: tone.surface }}
                        aria-label="Ver en plataforma"
                      >
                        {actionLabelForA}
                      </button>
                    ) : null}
                  </div>
                </div>

                {criteria.length ? (
                  <CriteriaWithBars
                    title="Por qué lo recomendamos"
                    criteria={criteria}
                    metrics={
                      currentOptimization?.metrics_extended && typeof currentOptimization.metrics_extended === 'object'
                        ? currentOptimization.metrics_extended
                        : null
                    }
                    currency={currency}
                    intentTone={intent}
                    accountBenchmarks={currentOptimization?.account_benchmarks || null}
                  />
                ) : null}

                {actionType === 'SPLIT_TOP_SELLERS' ? null : (
                  <ContextMetricsBar
                    analysisWindow={currentOptimization?.evidence?.analysis_window || null}
                    metrics={
                      // Ground header metrics in the suggestion payload first;
                      // falling back to visibleItems[0] for legacy single-item
                      // archetype A flows. For SPLIT_TOP_SELLERS the bottom
                      // merged table already carries the per-product ROAS, so
                      // we suppress this row entirely to avoid surfacing an
                      // item-level value as a suggestion-level header.
                      (currentOptimization?.metrics_extended && typeof currentOptimization.metrics_extended === 'object')
                        ? currentOptimization.metrics_extended
                        : ((visibleItems[0]?.metrics_extended && typeof visibleItems[0].metrics_extended === 'object')
                          ? visibleItems[0].metrics_extended
                          : (visibleItems[0]?.metrics && typeof visibleItems[0].metrics === 'object'
                            ? visibleItems[0].metrics
                            : {}))
                    }
                    currency={currency}
                    metricKeysToShow={metricKeysToShow}
                    className="mt-3"
                  />
                )}
              </div>
            );
          })()}

          {String(currentOptimization?.archetype || '').toUpperCase() === 'E' && (() => {
            const visibleItems = resolveVisibleItems(currentOptimization);
            const itemsCount = visibleItems.length;
            const isMultiItem = itemsCount > 1;
            const intent = currentOptimization?.intent;
            const currency =
              currentOptimization?.metrics_extended?.currency ||
              getCurrencyCodeForSuggestion(currentOptimization) ||
              'USD';

            const criteria = Array.isArray(currentOptimization?.criteria) ? currentOptimization.criteria : [];
            const ruleKeyLowerE = String(currentOptimization?.rule_key || currentOptimization?.rule?.key || '').toLowerCase();
            const isMetaN11PauseAdCtrDecline = ruleKeyLowerE === 'meta_n11_pause_ad_ctr_decline';
            const ctrWindows = currentOptimization?.window_comparison || null;
            const dailySeries = currentOptimization?.daily_series || null;
            const cpaStreak =
              currentOptimization?.evidence?.cpa_streak ||
              buildCpaStreakFromDailySeries(dailySeries);
            const roasStreak =
              currentOptimization?.evidence?.roas_streak ||
              buildRoasStreakFromDailySeries(dailySeries);
            const eActionLabel = isMetaN11PauseAdCtrDecline
              ? 'Revisar anuncio'
              : (currentOptimization?.action_label || null);

            const metricKeysToShow = (() => {
              const fromBackend = Array.isArray(currentOptimization?.metric_keys_to_show) ? currentOptimization.metric_keys_to_show : null;
              if (fromBackend) return fromBackend.slice(0, 8);
              const base = ['spend', 'conversions', 'cpa'];
              const fromCriteria = criteria
                .map((c) => c?.metric_key)
                .filter((k) => k && !base.includes(k) && k !== 'budget_utilization');
              if (isMetaN11PauseAdCtrDecline) {
                const extra = [
                  'inline_link_click_ctr_7d',
                  'inline_link_click_ctr_prev_7d',
                  'inline_link_click_ctr_ratio_7d_prev_7d',
                ].filter((k) => !base.includes(k) && !fromCriteria.includes(k));
                return [...base, ...fromCriteria, ...extra];
              }
              return [...base, ...fromCriteria];
            })();

            const tone = getIntentTone(intent);
            const actionType = String(currentOptimization?.action_type || '').trim().toUpperCase();
            const isMetaReviewOnly = isMetaReviewOnlyActionType(actionType);
            const isGoogleReviewOnly = isGoogleReviewOnlyActionType(actionType);
            const isReviewOnly = isMetaReviewOnly || isGoogleReviewOnly;
            const statusUpper = String(currentOptimization?.status || '').toUpperCase();
            const isFailed = statusUpper === 'FAILED';
            const canShowApplyCta = !currentOptimization?.status || ['NEW', 'SHOWN'].includes(statusUpper);
            const isAlreadyApplied = statusUpper === 'APPLIED';
            const canTriggerAction = isReviewOnly || canShowApplyCta || isAlreadyApplied;

            return (
              <div className="mb-6">
                {/* Header de acción */}
                <div className="mb-4 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                  <div className="px-4 py-3 flex items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <CreativePreview
                        entity={currentOptimization?.primary_entity || visibleItems?.[0]?.target_entity || null}
                        subtitle={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                        size="lg"
                      />
                    </div>
                    {isFailed ? renderFailedCta(currentOptimization) : eActionLabel ? (
                      <button
                        type="button"
                        disabled={!canTriggerAction || isAlreadyApplied || isApplyingSuggestion(currentOptimization?.id)}
                        onClick={() => {
                          if (!currentOptimization) return;
                          if (!canTriggerAction) return;
                          if (isAlreadyApplied) return;
                          if (isReviewOnly) {
                            openSuggestionDestination(currentOptimization, {
                              entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                              campaignId: currentOptimization?.campaign_groups?.[0]?.campaign_id ?? null,
                            });
                            return;
                          }
                          applyAndOpenDestination(currentOptimization, {
                            entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                          });
                        }}
                        className="shrink-0 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border border-white/10 disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 transition"
                        style={{ background: tone.pillBg, color: tone.surface }}
                        aria-label={isReviewOnly ? 'Ver en plataforma' : eActionLabel}
                      >
                        {isAlreadyApplied
                          ? 'Ya aplicaste'
                          : isApplyingSuggestion(currentOptimization?.id)
                            ? 'Aplicando…'
                            : eActionLabel}
                      </button>
                    ) : null}
                  </div>
                </div>

                {/* Por qué lo recomendamos */}
                {criteria.length &&
                !cpaStreak &&
                !roasStreak &&
                !ctrWindows ? (
                  <div className="mb-2">
                    <CriteriaWithBars
                      title="Por qué lo recomendamos"
                      criteria={criteria}
                      metrics={
                        currentOptimization?.metrics_extended && typeof currentOptimization.metrics_extended === 'object'
                          ? currentOptimization.metrics_extended
                          : null
                      }
                      currency={currency}
                      intentTone={intent}
                      accountBenchmarks={currentOptimization?.account_benchmarks || null}
                    />
                  </div>
                ) : null}

                {/* Contexto: si hay gráfico de racha o ventanas de CTR, priorizamos ese bloque y evitamos redundancia */}
                {!cpaStreak &&
                !roasStreak &&
                !ctrWindows ? (
                  <ContextMetricsBar
                    analysisWindow={currentOptimization?.evidence?.analysis_window || null}
                    metrics={
                      (visibleItems[0]?.metrics_extended && typeof visibleItems[0].metrics_extended === 'object')
                        ? visibleItems[0].metrics_extended
                        : ((visibleItems[0]?.metrics && typeof visibleItems[0].metrics === 'object')
                          ? visibleItems[0].metrics
                          : (currentOptimization?.metrics_extended || {}))
                    }
                    currency={currency}
                    metricKeysToShow={metricKeysToShow}
                    className="mt-3"
                  />
                ) : null}

                {isMetaN11PauseAdCtrDecline && ctrWindows ? <CtrWindowsChart windowComparison={ctrWindows} /> : null}

                {cpaStreak ? (
                  <CpaStreakChart streak={cpaStreak} currency={currency} />
                ) : null}
                {roasStreak ? (
                  <RoasStreakChart streak={roasStreak} />
                ) : null}

                {(cpaStreak ||
                  roasStreak ||
                  (isMetaN11PauseAdCtrDecline && ctrWindows)) &&
                currentOptimization?.evidence?.analysis_window ? (
                  <div className="mt-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-white/60">
                    {(() => {
                      const aw = currentOptimization.evidence.analysis_window;
                      const start = aw?.start ? new Date(aw.start) : null;
                      const end = aw?.end ? new Date(aw.end) : null;
                      if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                        const fmt = new Intl.DateTimeFormat('es-UY', { day: 'numeric', month: 'short' });
                        return `Período: Del ${fmt.format(start)} al ${fmt.format(end)}`;
                      }
                      if (aw?.label) return `Período: ${String(aw.label)}`;
                      return 'Período: —';
                    })()}
                  </div>
                ) : null}

                {currentOptimization?.user_text ? (
                  <div className="mt-3">
                    <UserTextAccordion title="Detalle" html={currentOptimization.user_text} defaultOpen={false} />
                  </div>
                ) : null}

                {/* Comparativa con otros ads (mismo adset) */}
                {(() => {
                  const peerAds = currentOptimization?.evidence?.peer_ads;
                  if (!Array.isArray(peerAds) || peerAds.length === 0) return null;
                  const enriched = peerAds.slice(0, 4).map((row, idx, arr) => ({
                    ...row,
                    __isCurrent:
                      String(row?.ad_name || '').toLowerCase().includes('este anuncio') ||
                      idx === arr.length - 1,
                  }));
                  return (
                    <EntityTable
                      title="Comparativa en el mismo AdSet"
                      description="Compará contra anuncios que están ganando subastas y volumen."
                      rows={enriched}
                      getRowKey={(r, i) => `${r?.ad_name ?? 'ad'}-${i}`}
                      getRowVariant={(r) => (r.__isCurrent ? 'highlight' : 'successSubtle')}
                      columns={[
                        {
                          key: 'ad',
                          label: 'Anuncio',
                          render: (r) => {
                            const name = r?.ad_name ?? 'Anuncio';
                            return (
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="text-sm font-semibold text-white truncate" title={name}>{name}</div>
                                <StatusPill
                                  label={r.__isCurrent ? 'Este anuncio' : 'Ganador'}
                                  tone={r.__isCurrent ? 'warning' : 'success'}
                                />
                              </div>
                            );
                          },
                        },
                        {
                          key: 'spend',
                          label: 'Gasto',
                          align: 'right',
                          render: (r) => (
                            <MetricSingle
                              value={r?.spend != null ? formatCurrencyNoDecimals(r.spend, currency) : null}
                              size="sm"
                            />
                          ),
                        },
                        {
                          key: 'roas',
                          label: 'ROAS',
                          align: 'right',
                          render: (r) => (
                            r?.roas != null
                              ? <span className={`text-sm font-semibold tabular-nums whitespace-nowrap ${r.__isCurrent ? 'text-amber-100' : 'text-emerald-100'}`}>{`${Number(r.roas).toFixed(1)}x`}</span>
                              : '—'
                          ),
                        },
                        {
                          key: 'conv',
                          label: 'Conv.',
                          align: 'right',
                          render: (r) => (
                            <MetricSingle value={r?.conversions ?? null} hint="integer" size="sm" />
                          ),
                        },
                      ]}
                    />
                  );
                })()}

                {/* Items list (solo si multi) */}
                {isMultiItem ? (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setIsEItemsExpanded((v) => !v)}
                      className="w-full flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:bg-white/7 transition-colors"
                    >
                      <div className="text-left min-w-0">
                        <div className="text-xs font-semibold text-white/80">
                          {isEItemsExpanded ? 'Ocultar' : 'Ver'} {itemsCount} items
                        </div>
                        <div className="mt-0.5 text-[11px] text-white/55 truncate">
                          Revisá las entidades sugeridas para esta acción
                        </div>
                      </div>
                      <div className="text-white/60 text-lg shrink-0" aria-hidden>
                        {isEItemsExpanded ? '▴' : '▾'}
                      </div>
                    </button>

                    {isEItemsExpanded ? (
                      <div className="mt-2 flex flex-col gap-2">
                        {visibleItems.map((item, idx) => (
                          <ItemCard
                            key={item?.id ?? idx}
                            item={item}
                            suggestionCriteria={criteria}
                            intent={intent}
                            currency={currency}
                            suggestionBreadcrumb={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                            isApplied={Boolean(item?.applied_at)}
                            onApply={null}
                            suggestionMetricKeysToShow={metricKeysToShow}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })()}

          {String(currentOptimization?.archetype || '').toUpperCase() === 'H' && (() => {
            const visibleItems = resolveVisibleItems(currentOptimization);
            const intent = currentOptimization?.intent;
            const currency =
              currentOptimization?.metrics_extended?.currency ||
              getCurrencyCodeForSuggestion(currentOptimization) ||
              'USD';
            const criteria = Array.isArray(currentOptimization?.criteria) ? currentOptimization.criteria : [];
            const metricKeysToShow = resolveMetricKeysToShow(
              currentOptimization,
              criteria,
              ['spend_7d', 'conversions_7d', 'roas_7d', 'roas_30d', 'cpa_7d', 'cpa_30d'],
            );
            const tone = getIntentTone(intent);
            const actionType = String(currentOptimization?.action_type || '').trim().toUpperCase();
            const isMetaReviewOnly = isMetaReviewOnlyActionType(actionType);
            const statusUpper = String(currentOptimization?.status || '').toUpperCase();
            const isFailed = statusUpper === 'FAILED';
            const canShowApplyCta = !currentOptimization?.status || ['NEW', 'SHOWN'].includes(statusUpper);
            const isAlreadyApplied = statusUpper === 'APPLIED';
            const canTrigger = isMetaReviewOnly || canShowApplyCta || isAlreadyApplied;

            return (
              <div className="mb-6">
                <div className="mb-4 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                  <div className="px-4 py-3 flex items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <CreativePreview
                        entity={currentOptimization?.primary_entity || visibleItems?.[0]?.target_entity || null}
                        subtitle={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                        size="lg"
                      />
                    </div>
                    {isFailed ? renderFailedCta(currentOptimization) : currentOptimization?.action_label ? (
                      <button
                        type="button"
                        disabled={!canTrigger || isAlreadyApplied || isApplyingSuggestion(currentOptimization?.id)}
                        onClick={async () => {
                          if (!currentOptimization) return;
                          if (!canTrigger) return;
                          if (isAlreadyApplied) return;
                          const destinationParams = {
                            entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                            campaignId: currentOptimization?.campaign_groups?.[0]?.campaign_id ?? null,
                          };
                          if (currentOptimization?.structural_change?.requires_review) {
                            setPendingHReview({ opt: currentOptimization, destinationParams });
                            return;
                          }
                          await applySuggestion(currentOptimization, { execute: true });
                          openSuggestionDestination(currentOptimization, destinationParams);
                        }}
                        className="shrink-0 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border border-white/10 disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 transition"
                        style={{ background: tone.pillBg, color: tone.surface }}
                        aria-label={currentOptimization?.action_label || 'Ver en Meta'}
                      >
                        {currentOptimization?.action_label || 'Ver en Meta'}
                      </button>
                    ) : null}
                  </div>
                </div>

                {criteria.length ? (
                  <div className="mt-3">
                    <CriteriaWithBars
                      title="Por qué lo recomendamos"
                      criteria={criteria}
                      metrics={
                        currentOptimization?.metrics_extended && typeof currentOptimization.metrics_extended === 'object'
                          ? currentOptimization.metrics_extended
                          : null
                      }
                      currency={currency}
                      intentTone={intent}
                      accountBenchmarks={currentOptimization?.account_benchmarks || null}
                    />
                  </div>
                ) : null}

                <ContextMetricsBar
                  analysisWindow={currentOptimization?.evidence?.analysis_window || null}
                  metrics={
                    (visibleItems[0]?.metrics_extended && typeof visibleItems[0].metrics_extended === 'object')
                      ? visibleItems[0].metrics_extended
                      : ((visibleItems[0]?.metrics && typeof visibleItems[0].metrics === 'object')
                        ? visibleItems[0].metrics
                        : (currentOptimization?.metrics_extended || {}))
                  }
                  currency={currency}
                  metricKeysToShow={metricKeysToShow}
                  className="mt-3"
                />

                {(() => {
                  const pairs = currentOptimization?.evidence?.auction_overlap_pairs;
                  if (!Array.isArray(pairs) || pairs.length === 0) return null;
                  return (
                    <EntityTable
                      title="Overlaps entre AdSets"
                      description="Cuando dos AdSets se superponen, terminan pujando entre sí y se encarece el delivery."
                      rows={pairs}
                      maxRows={6}
                      getRowKey={(r, i) => `${r?.adset_a_name ?? 'a'}-${r?.adset_b_name ?? 'b'}-${i}`}
                      columns={[
                        {
                          key: 'adset_a',
                          label: 'AdSet A',
                          colSpan: 2,
                          render: (r) => {
                            const a = r?.adset_a_name ?? 'AdSet A';
                            return <div className="text-sm font-semibold text-white truncate" title={a}>{a}</div>;
                          },
                        },
                        {
                          key: 'adset_b',
                          label: 'AdSet B',
                          colSpan: 2,
                          render: (r) => {
                            const b = r?.adset_b_name ?? 'AdSet B';
                            return <div className="text-sm font-semibold text-white truncate" title={b}>{b}</div>;
                          },
                        },
                        {
                          key: 'overlap',
                          label: 'Overlap',
                          align: 'right',
                          render: (r) => {
                            const pct = typeof r?.overlap_pct === 'number' ? r.overlap_pct : Number(r?.overlap_pct);
                            if (!Number.isFinite(pct)) return '—';
                            return (
                              <StatusPill
                                label={`${(pct * 100).toFixed(0)}%`}
                                tone={pct >= 0.4 ? 'danger' : 'warning'}
                                tabularNums
                              />
                            );
                          },
                        },
                        {
                          key: 'spend',
                          label: 'Gasto',
                          align: 'right',
                          render: (r) => {
                            const hasSpend = r?.spend_a != null || r?.spend_b != null;
                            const value = hasSpend
                              ? formatCurrencyNoDecimals(Number(r?.spend_a || 0) + Number(r?.spend_b || 0), currency)
                              : null;
                            return <MetricSingle value={value} size="sm" />;
                          },
                        },
                        {
                          key: 'roas',
                          label: 'ROAS',
                          align: 'right',
                          render: (r) => {
                            const text =
                              r?.roas_a != null && r?.roas_b != null
                                ? `${(((Number(r.roas_a) + Number(r.roas_b)) / 2) || 0).toFixed(1)}x`
                                : r?.roas_a != null
                                  ? `${Number(r.roas_a).toFixed(1)}x`
                                  : r?.roas_b != null
                                    ? `${Number(r.roas_b).toFixed(1)}x`
                                    : null;
                            return <MetricSingle value={text} size="sm" />;
                          },
                        },
                      ]}
                    />
                  );
                })()}

                {(() => {
                  const segments = currentOptimization?.evidence?.audience_segments;
                  if (!Array.isArray(segments) || segments.length === 0) return null;
                  return (
                    <EntityTable
                      title="Audiencia"
                      description="Compará el tamaño de tu segmentación actual vs la opción Broad."
                      rows={segments}
                      maxRows={6}
                      getRowKey={(s, i) => `${s?.segment_label ?? 'seg'}-${i}`}
                      getRowVariant={(s) => (String(s?.segment_label || '').toLowerCase().includes('broad') ? 'success' : 'default')}
                      columns={[
                        {
                          key: 'segment',
                          label: 'Segmento',
                          colSpan: 2,
                          render: (s) => {
                            const label = s?.segment_label ?? 'Segmento';
                            return (
                              <>
                                <div className="text-sm font-semibold text-white truncate" title={label}>{label}</div>
                                {s?.note ? (
                                  <div className="mt-0.5 text-[11px] text-white/55 truncate" title={s.note}>{String(s.note)}</div>
                                ) : null}
                              </>
                            );
                          },
                        },
                        {
                          key: 'size',
                          label: 'Tamaño',
                          align: 'right',
                          render: (s) => (
                            <MetricSingle value={s?.audience_size ?? null} hint="integer" size="sm" />
                          ),
                        },
                        {
                          key: 'spend',
                          label: 'Gasto',
                          align: 'right',
                          render: (s) => (
                            <MetricSingle
                              value={s?.spend_7d != null ? formatCurrencyNoDecimals(s.spend_7d, currency) : null}
                              size="sm"
                            />
                          ),
                        },
                        {
                          key: 'conv',
                          label: 'Conv.',
                          align: 'right',
                          render: (s) => (
                            <MetricSingle value={s?.conversions_7d ?? null} hint="integer" size="sm" />
                          ),
                        },
                        {
                          key: 'cpa',
                          label: 'CPA',
                          align: 'right',
                          render: (s) => (
                            <MetricSingle
                              value={s?.cpa_7d != null ? formatCurrencyNoDecimals(s.cpa_7d, currency) : null}
                              size="sm"
                            />
                          ),
                        },
                      ]}
                    />
                  );
                })()}

                {currentOptimization?.user_text ? (
                  <div className="mt-3">
                    <UserTextAccordion title="Detalle" html={currentOptimization.user_text} defaultOpen={false} />
                  </div>
                ) : null}
              </div>
            );
          })()}

          {String(currentOptimization?.archetype || '').toUpperCase() === 'B' && (() => {
            const visibleItems = resolveVisibleItems(currentOptimization);
            const intent = currentOptimization?.intent;
            const currency =
              currentOptimization?.metrics_extended?.currency ||
              getCurrencyCodeForSuggestion(currentOptimization) ||
              'USD';
            const criteria = Array.isArray(currentOptimization?.criteria) ? currentOptimization.criteria : [];
            const metricKeysToShow = resolveMetricKeysToShow(
              currentOptimization,
              criteria,
              ['spend_7d', 'conversions_7d', 'cpa_7d', 'cpa_30d', 'roas_7d', 'roas_30d'],
            );
            const tone = getIntentTone(intent);
            const actionType = String(currentOptimization?.action_type || '').trim().toUpperCase();
            const isMetaReviewOnly = isMetaReviewOnlyActionType(actionType);
            const statusUpper = String(currentOptimization?.status || '').toUpperCase();
            const isFailed = statusUpper === 'FAILED';
            const canShowApplyCta = !currentOptimization?.status || ['NEW', 'SHOWN'].includes(statusUpper);
            const isAlreadyApplied = statusUpper === 'APPLIED';
            const canTrigger = isMetaReviewOnly || canShowApplyCta || isAlreadyApplied;

            return (
              <div className="mb-6">
                <div className="mb-4 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                  <div className="px-4 py-3 flex items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <CreativePreview
                        entity={currentOptimization?.primary_entity || visibleItems?.[0]?.target_entity || null}
                        subtitle={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                        size="lg"
                      />
                    </div>
                    {isFailed ? renderFailedCta(currentOptimization) : (
                    <button
                      type="button"
                      disabled={!canTrigger || isAlreadyApplied || isApplyingSuggestion(currentOptimization?.id)}
                      onClick={() => {
                        if (!currentOptimization) return;
                        if (!canTrigger) return;
                        if (isAlreadyApplied) return;
                        if (isMetaReviewOnly) {
                          openSuggestionDestination(currentOptimization, {
                            entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                            campaignId: currentOptimization?.campaign_groups?.[0]?.campaign_id ?? null,
                          });
                          return;
                        }
                        applyAndOpenDestination(currentOptimization, {
                          entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                        });
                      }}
                      className="shrink-0 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border border-white/10 disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 transition"
                      style={{ background: tone.pillBg, color: tone.surface }}
                      aria-label={currentOptimization?.action_label || (isMetaReviewOnly ? 'Ver en plataforma' : 'Aplicar')}
                    >
                      {isAlreadyApplied ? 'Ya aplicaste' : (currentOptimization?.action_label || (isMetaReviewOnly ? 'Ver en Meta' : 'Aplicar'))}
                    </button>
                    )}
                  </div>
                </div>

                {criteria.length ? (
                  <CriteriaWithBars
                    title="Por qué lo recomendamos"
                    criteria={criteria}
                    metrics={
                      currentOptimization?.metrics_extended && typeof currentOptimization.metrics_extended === 'object'
                        ? currentOptimization.metrics_extended
                        : null
                    }
                    currency={currency}
                    intentTone={intent}
                    accountBenchmarks={currentOptimization?.account_benchmarks || null}
                  />
                ) : null}

                <WindowComparisonCard windowComparison={currentOptimization?.window_comparison || null} intent={intent} currency={currency} />

                <ContextMetricsBar
                  analysisWindow={currentOptimization?.evidence?.analysis_window || null}
                  metrics={
                    (visibleItems[0]?.metrics_extended && typeof visibleItems[0].metrics_extended === 'object')
                      ? visibleItems[0].metrics_extended
                      : ((visibleItems[0]?.metrics && typeof visibleItems[0].metrics === 'object')
                        ? visibleItems[0].metrics
                        : (currentOptimization?.metrics_extended || {}))
                  }
                  currency={currency}
                  metricKeysToShow={metricKeysToShow}
                  className="mt-3"
                />

                {currentOptimization?.user_text ? (
                  <div className="mt-3">
                    <UserTextAccordion title="Detalle" html={currentOptimization.user_text} defaultOpen={false} />
                  </div>
                ) : null}
              </div>
            );
          })()}

          {String(currentOptimization?.archetype || '').toUpperCase() === 'F' && (() => {
            const visibleItems = resolveVisibleItems(currentOptimization);
            const intent = currentOptimization?.intent;
            const currency =
              currentOptimization?.metrics_extended?.currency ||
              getCurrencyCodeForSuggestion(currentOptimization) ||
              'USD';
            const tone = getIntentTone(intent);
            const actionType = String(currentOptimization?.action_type || '').trim().toUpperCase();
            const isMetaReviewOnly = isMetaReviewOnlyActionType(actionType);
            const isGoogleReviewOnly = isGoogleReviewOnlyActionType(actionType);
            const isReviewOnly = isMetaReviewOnly || isGoogleReviewOnly;
            const statusUpper = String(currentOptimization?.status || '').toUpperCase();
            const isFailed = statusUpper === 'FAILED';
            const canShowApplyCta = !currentOptimization?.status || ['NEW', 'SHOWN'].includes(statusUpper);
            const isAlreadyApplied = statusUpper === 'APPLIED';
            const canTrigger = isReviewOnly || canShowApplyCta || isAlreadyApplied;
            const subItems = Array.isArray(currentOptimization?.sub_items) ? currentOptimization.sub_items : [];
            const subItemsSummary = currentOptimization?.sub_items_summary || null;
            const criteria = Array.isArray(currentOptimization?.criteria) ? currentOptimization.criteria : [];
            const selectedSubItemIds = getSubItemSelection(currentOptimization?.id, subItems);
            const handleSubItemToggle = (id) => toggleSubItemSelection(currentOptimization?.id, subItems, id);
            const hasSelectedSubItems = selectedSubItemIds.length > 0;
            const metricKeysToShow = resolveMetricKeysToShow(currentOptimization, criteria);

            return (
              <div className="mb-6">
                <div className="mb-4 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                  <div className="px-4 py-3 flex items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <CreativePreview
                        entity={currentOptimization?.primary_entity || visibleItems?.[0]?.target_entity || null}
                        subtitle={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                        size="lg"
                      />
                    </div>
                    {isFailed ? renderFailedCta(currentOptimization) : (
                    <button
                      type="button"
                      disabled={
                        !canTrigger
                        || isAlreadyApplied
                        || isApplyingSuggestion(currentOptimization?.id)
                        || (!isReviewOnly && !hasSelectedSubItems)
                      }
                      onClick={async () => {
                        if (!currentOptimization) return;
                        if (!canTrigger) return;
                        if (isAlreadyApplied) return;
                        const destinationParams = {
                          entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                          campaignId: currentOptimization?.campaign_groups?.[0]?.campaign_id ?? null,
                        };
                        if (isReviewOnly) {
                          openSuggestionDestination(currentOptimization, destinationParams);
                          return;
                        }
                        if (!hasSelectedSubItems) {
                          toast.error('Seleccioná al menos un ítem para aplicar.');
                          return;
                        }
                        const result = await applySuggestion(currentOptimization, {
                          execute: true,
                          execution_params: { selected_sub_item_ids: selectedSubItemIds },
                        });
                        if (!result?.execution?.executed) {
                          openSuggestionDestination(currentOptimization, destinationParams);
                        }
                      }}
                      className="shrink-0 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border border-white/10 disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 transition"
                      style={{ background: tone.pillBg, color: tone.surface }}
                      aria-label={currentOptimization?.action_label || (isReviewOnly ? 'Ver en plataforma' : 'Aplicar')}
                    >
                      {isAlreadyApplied
                        ? 'Ya aplicaste'
                        : (currentOptimization?.action_label || (isReviewOnly
                          ? (isMetaReviewOnly ? 'Ver en Meta' : 'Ver en Google')
                          : 'Aplicar'))}
                    </button>
                    )}
                  </div>
                </div>

                {criteria.length ? (
                  <CriteriaWithBars
                    title="Por qué lo recomendamos"
                    criteria={criteria}
                    metrics={
                      currentOptimization?.metrics_extended && typeof currentOptimization.metrics_extended === 'object'
                        ? currentOptimization.metrics_extended
                        : null
                    }
                    currency={currency}
                    intentTone={intent}
                    accountBenchmarks={currentOptimization?.account_benchmarks || null}
                  />
                ) : null}

                {isPlacementsSubItems(subItems) ? (
                  <PlacementsSubItemsTable
                    subItems={subItems}
                    subItemsSummary={subItemsSummary}
                    currency={currency}
                    selectedIds={isReviewOnly ? null : selectedSubItemIds}
                    onToggle={isReviewOnly ? null : handleSubItemToggle}
                  />
                ) : (
                  <SubItemsTable
                    subItems={subItems}
                    subItemsSummary={subItemsSummary}
                    currency={currency}
                    primaryKpi={currentOptimization?.primary_kpi || currentOptimization?.primary_kpi_name || 'ROAS'}
                    selectedIds={isReviewOnly ? null : selectedSubItemIds}
                    onToggle={isReviewOnly ? null : handleSubItemToggle}
                  />
                )}

                <ContextMetricsBar
                  analysisWindow={currentOptimization?.evidence?.analysis_window || null}
                  metrics={
                    (visibleItems[0]?.metrics_extended && typeof visibleItems[0].metrics_extended === 'object')
                      ? visibleItems[0].metrics_extended
                      : ((visibleItems[0]?.metrics && typeof visibleItems[0].metrics === 'object')
                        ? visibleItems[0].metrics
                        : (currentOptimization?.metrics_extended || {}))
                  }
                  currency={currency}
                  metricKeysToShow={metricKeysToShow}
                  className="mt-3"
                />

                {currentOptimization?.user_text ? (
                  <div className="mt-3">
                    <UserTextAccordion title="Detalle" html={currentOptimization.user_text} defaultOpen={false} />
                  </div>
                ) : null}
              </div>
            );
          })()}

          {String(currentOptimization?.archetype || '').toUpperCase() === 'G' && (() => {
            const visibleItems = resolveVisibleItems(currentOptimization);
            const intent = currentOptimization?.intent;
            const currency =
              currentOptimization?.metrics_extended?.currency ||
              getCurrencyCodeForSuggestion(currentOptimization) ||
              'USD';
            const tone = getIntentTone(intent);
            const actionType = String(currentOptimization?.action_type || '').trim().toUpperCase();
            const isMetaReviewOnly = isMetaReviewOnlyActionType(actionType);
            const statusUpper = String(currentOptimization?.status || '').toUpperCase();
            const isFailed = statusUpper === 'FAILED';
            const canShowApplyCta = !currentOptimization?.status || ['NEW', 'SHOWN'].includes(statusUpper);
            const isAlreadyApplied = statusUpper === 'APPLIED';
            const canTrigger = isMetaReviewOnly || canShowApplyCta || isAlreadyApplied;
            const diag = currentOptimization?.diagnostic_score || null;
            const criteria = Array.isArray(currentOptimization?.criteria) ? currentOptimization.criteria : [];
            const metricKeysToShow = resolveMetricKeysToShow(
              currentOptimization,
              criteria,
              ['roas_14d', 'budget_utilization', 'fatigue_score', 'scaling_score'],
            );

            return (
              <div className="mb-6">
                <div className="mb-4 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                  <div className="px-4 py-3 flex items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <CreativePreview
                        entity={currentOptimization?.primary_entity || visibleItems?.[0]?.target_entity || null}
                        subtitle={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                        size="lg"
                      />
                    </div>
                    {isFailed ? renderFailedCta(currentOptimization) : (
                    <button
                      type="button"
                      disabled={!canTrigger || isAlreadyApplied || isApplyingSuggestion(currentOptimization?.id)}
                      onClick={() => {
                        if (!currentOptimization) return;
                        if (!canTrigger) return;
                        if (isAlreadyApplied) return;
                        applyAndOpenDestination(currentOptimization, {
                          entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                          campaignId: currentOptimization?.campaign_groups?.[0]?.campaign_id ?? null,
                        });
                      }}
                      className="shrink-0 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border border-white/10 disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 transition"
                      style={{ background: tone.pillBg, color: tone.surface }}
                      aria-label={currentOptimization?.action_label || 'Ver en Meta'}
                    >
                      {currentOptimization?.action_label || 'Ver en Meta'}
                    </button>
                    )}
                  </div>
                </div>

                <DiagnosticScoreCard diagnosticScore={diag} intent={intent} />

                {criteria.length ? (
                  <div className="mt-3">
                    <CriteriaWithBars
                      title="Señales detectadas"
                      criteria={criteria}
                      metrics={
                        currentOptimization?.metrics_extended && typeof currentOptimization.metrics_extended === 'object'
                          ? currentOptimization.metrics_extended
                          : null
                      }
                      currency={currency}
                      intentTone={intent}
                      accountBenchmarks={currentOptimization?.account_benchmarks || null}
                    />
                  </div>
                ) : null}

                <ContextMetricsBar
                  analysisWindow={currentOptimization?.evidence?.analysis_window || null}
                  metrics={
                    (visibleItems[0]?.metrics_extended && typeof visibleItems[0].metrics_extended === 'object')
                      ? visibleItems[0].metrics_extended
                      : ((visibleItems[0]?.metrics && typeof visibleItems[0].metrics === 'object')
                        ? visibleItems[0].metrics
                        : (currentOptimization?.metrics_extended || {}))
                  }
                  currency={currency}
                  metricKeysToShow={metricKeysToShow}
                  className="mt-3"
                />

                {currentOptimization?.user_text ? (
                  <div className="mt-3">
                    <UserTextAccordion title="Detalle" html={currentOptimization.user_text} defaultOpen={false} />
                  </div>
                ) : null}
              </div>
            );
          })()}

          {String(currentOptimization?.archetype || '').toUpperCase() === 'C' && (() => {
            const visibleItems = resolveVisibleItems(currentOptimization);
            const intent = currentOptimization?.intent;
            const currency =
              currentOptimization?.metrics_extended?.currency ||
              getCurrencyCodeForSuggestion(currentOptimization) ||
              'USD';

            const criteria = Array.isArray(currentOptimization?.criteria) ? currentOptimization.criteria : [];
            const ruleKeyLowerC = String(currentOptimization?.rule_key || currentOptimization?.rule?.key || '').toLowerCase();
            const isMetaN12CpaSpike48h = ruleKeyLowerC === 'meta_n12_alert_adset_cpa_spike_48h';

            const metricKeysToShow = (() => {
              const fromBackend = Array.isArray(currentOptimization?.metric_keys_to_show) ? currentOptimization.metric_keys_to_show : null;
              if (fromBackend) return fromBackend.slice(0, 8);
              if (isMetaN12CpaSpike48h) {
                return ['cpa_48h', 'cpa_14d'];
              }
              const base = ['spend', 'conversions', 'cpa', 'roas', 'frequency', 'inline_link_click_ctr'];
              const fromCriteria = criteria
                .map((c) => c?.metric_key)
                .filter((k) => k && !base.includes(k) && k !== 'budget_utilization');
              return [...base, ...fromCriteria];
            })();

            const wc = currentOptimization?.window_comparison || null;
            const wcSeverity = String(wc?.alert_severity || '').toLowerCase();
            const severityChip =
              wcSeverity === 'high'
                ? { bg: 'rgba(245,158,11,0.18)', border: 'rgba(245,158,11,0.30)', text: 'rgba(255,251,235,0.95)' }
                : { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.10)', text: 'rgba(255,255,255,0.85)' };
            const cActionType = String(currentOptimization?.action_type || '').trim().toUpperCase();
            const isNoActionC = cActionType === 'NO_ACTION';
            const investigateLabel = currentOptimization?.action_label || 'Investigar';

            return (
              <div className="mb-6">
                {/* Header de alerta */}
                <div className="mb-4 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <CreativePreview
                        entity={currentOptimization?.primary_entity || visibleItems?.[0]?.target_entity || null}
                        subtitle={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                        size="lg"
                      />
                    </div>
                    <span
                      className="shrink-0 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border"
                      style={{
                        background: severityChip.bg,
                        borderColor: severityChip.border,
                        color: severityChip.text,
                      }}
                    >
                      Alerta
                    </span>
                    {isNoActionC ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (!currentOptimization) return;
                          openSuggestionDestination(currentOptimization, {
                            entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                            campaignId: currentOptimization?.campaign_groups?.[0]?.campaign_id ?? null,
                          });
                        }}
                        className="shrink-0 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap border border-white/15 bg-white/10 text-white hover:bg-white/15 transition"
                        aria-label={investigateLabel}
                      >
                        {investigateLabel}
                      </button>
                    ) : null}
                  </div>
                </div>

                {isMetaN12CpaSpike48h && wc ? (
                  <WindowComparisonCard
                    windowComparison={wc}
                    intent={intent}
                    currency={currency}
                    title="CPA entre ventanas"
                    subtitle="48 h frente al promedio de 14 días"
                  />
                ) : null}

                {criteria.length && !(isMetaN12CpaSpike48h && wc) ? (
                  <div className="mb-2">
                    <CriteriaWithBars
                      title="Por qué lo recomendamos"
                      criteria={criteria}
                      metrics={
                        currentOptimization?.metrics_extended && typeof currentOptimization.metrics_extended === 'object'
                          ? currentOptimization.metrics_extended
                          : null
                      }
                      currency={currency}
                      intentTone={intent}
                      accountBenchmarks={currentOptimization?.account_benchmarks || null}
                    />
                  </div>
                ) : null}

                {/* Evitamos duplicar la comparación de ventanas cuando ya se muestra dentro de los criterios */}

                <ContextMetricsBar
                  analysisWindow={currentOptimization?.evidence?.analysis_window || null}
                  metrics={
                    (visibleItems[0]?.metrics_extended && typeof visibleItems[0].metrics_extended === 'object')
                      ? visibleItems[0].metrics_extended
                      : ((visibleItems[0]?.metrics && typeof visibleItems[0].metrics === 'object')
                        ? visibleItems[0].metrics
                        : (currentOptimization?.metrics_extended || {}))
                  }
                  currency={currency}
                  metricKeysToShow={metricKeysToShow}
                  className="mt-3"
                />

                {currentOptimization?.user_text ? (
                  <div className="mt-3">
                    <UserTextAccordion
                      title="Detalle"
                      html={currentOptimization.user_text}
                      defaultOpen={false}
                    />
                  </div>
                ) : null}
              </div>
            );
          })()}

          {String(currentOptimization?.archetype || '').toUpperCase() === 'D' && (() => {
            const visibleItems = resolveVisibleItems(currentOptimization);
            const itemsCount = visibleItems.length;
            const isMultiItem = itemsCount > 1;
            const intent = currentOptimization?.intent;
            const currency =
              currentOptimization?.metrics_extended?.currency ||
              getCurrencyCodeForSuggestion(currentOptimization) ||
              'USD';

            if (currentOptimization?.value_change_display == null && !isMultiItem) {
              // Este arquetipo no debería llegar sin value_change_display
              // (pero degradamos sin romper UI)
              // eslint-disable-next-line no-console
              console.warn('[OptimizationDetailModal] archetype D sin value_change_display', currentOptimization?.id);
            }

            const types = visibleItems
              .map((it) => it?.target_entity?.type ?? it?.item_type)
              .filter(Boolean)
              .map((t) => String(t).toUpperCase());
            const uniqueTypes = [...new Set(types)];
            const entityTypePlural = (() => {
              if (uniqueTypes.length === 0) return 'ITEMS';
              if (uniqueTypes.length > 1) return 'ITEMS';
              const t = uniqueTypes[0];
              const PLURALS = {
                AD_GROUP: 'ADSETS',
                AD: 'ANUNCIOS',
                CAMPAIGN: 'CAMPAÑAS',
                KEYWORD: 'KEYWORDS',
                PLACEMENT: 'PLACEMENTS',
                OFFER: 'PRODUCTOS',
                PRODUCT: 'PRODUCTOS',
                ASSET: 'ASSETS',
                CREATIVE: 'CREATIVIDADES',
              };
              return PLURALS[t] || `${t}S`;
            })();

            const hasPerItemValueChange = isMultiItem && visibleItems.some((it) => it?.value_change != null);
            const fallbackToSingle = isMultiItem && !hasPerItemValueChange;
            const effectiveMulti = isMultiItem && !fallbackToSingle;

            const criteria = Array.isArray(currentOptimization?.criteria) ? currentOptimization.criteria : [];
            const metricKeysToShow = resolveMetricKeysToShow(
              currentOptimization,
              criteria,
              ['spend', 'conversions', 'cpa'],
            );

            const ruleKeyLower = String(currentOptimization?.rule_key || currentOptimization?.rule?.key || '').toLowerCase();
            const isMetaN8 = ruleKeyLower === 'meta_n8_decrease_budget_adset_cpa_rise_soft';
            const isMetaN9 = ruleKeyLower === 'meta_n9_decrease_budget_campaign_roas_decline_soft';
            const isMetaN10 = ruleKeyLower === 'meta_n10_decrease_budget_campaign_cpa_rise_soft';
            const wc = currentOptimization?.window_comparison || null;
            const wcMetricKey = String(wc?.metric_key || '').toLowerCase();
            const cpaWindows = wc && (wcMetricKey === 'cpa' || wcMetricKey.includes('cpa')) ? wc : null;
            const roasWindows = wc && (wcMetricKey === 'roas' || wcMetricKey.includes('roas')) ? wc : null;
            const targetCpa = currentOptimization?.metrics_extended?.target_cpa ?? currentOptimization?.target_cpa ?? null;
            const targetRoas = currentOptimization?.metrics_extended?.target_roas ?? currentOptimization?.target_roas ?? null;

            return (
              <div className="mb-6">
                {effectiveMulti ? (() => {
                  const tone = getIntentTone(intent);
                  return (
                    <div className="mb-5 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
                      <div className="px-4 py-3 flex items-center gap-4">
                        <div className="shrink-0 w-11 h-11 rounded-lg flex items-center justify-center border border-white/10" style={{ background: tone.bg }}>
                          <Layers className="w-[22px] h-[22px]" style={{ color: tone.text }} aria-hidden />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-medium tracking-[0.6px] uppercase text-white/50 leading-none">
                            {(entityTypePlural || 'ITEMS').toUpperCase()}
                          </div>
                          <div className="mt-[2px] text-[15px] font-medium text-white truncate">
                            {itemsCount} {(entityTypePlural || 'items').toLowerCase()} para revisar
                          </div>
                        </div>
                        {currentOptimization?.action_label ? (
                          <span
                            className="shrink-0 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border border-white/10"
                            style={{ background: tone.pillBg, color: tone.surface }}
                          >
                            {currentOptimization.action_label}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })() : (
                  <BeforeAfter
                    entity={currentOptimization?.primary_entity || null}
                    subtitle={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                    actionLabel={currentOptimization?.action_label || null}
                    intent={intent}
                    valueChange={currentOptimization?.value_change_display || null}
                    afterEditable={true}
                    valueAfterRaw={valueAfterBySuggestionId?.[currentOptimization?.id] ?? ''}
                    onChangeValueAfterRaw={(nextRaw) =>
                      setValueAfterBySuggestionId((prev) => ({ ...(prev || {}), [currentOptimization?.id]: nextRaw }))
                    }
                  />
                )}

                {/* CTA debajo del bloque de cambio (solo arquetipo D) */}
                {(() => {
                  const actionType = String(currentOptimization?.action_type || '').trim().toUpperCase();
                  const isMetaReviewOnly = isMetaReviewOnlyActionType(actionType);
                  const isGoogleReviewOnly = isGoogleReviewOnlyActionType(actionType);
                  const isReviewOnly = isMetaReviewOnly || isGoogleReviewOnly;
                  const statusUpper = String(currentOptimization?.status || '').toUpperCase();
                  const isCtaFailed = statusUpper === 'FAILED';
                  const canShowApplyCta = !currentOptimization?.status || ['NEW', 'SHOWN'].includes(statusUpper);
                  const isAlreadyApplied = statusUpper === 'APPLIED';
                  if (!isReviewOnly && !canShowApplyCta && !isAlreadyApplied && !isCtaFailed) return null;
                  if (isCtaFailed) {
                    return (
                      <div className="mt-3 flex justify-end">
                        {renderFailedCta(currentOptimization)}
                      </div>
                    );
                  }
                  return (
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        disabled={isAlreadyApplied || isApplyingSuggestion(currentOptimization?.id)}
                        onClick={() => {
                          if (!currentOptimization) return;
                          if (isAlreadyApplied) return;
                          if (isReviewOnly) {
                            openSuggestionDestination(currentOptimization, {
                              entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                              campaignId: currentOptimization?.campaign_groups?.[0]?.campaign_id ?? null,
                            });
                            return;
                          }
                          applyAndOpenDestination(currentOptimization, {
                            entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                          });
                        }}
                        className={`min-h-[36px] rounded-lg px-3 py-2 text-xs font-semibold border transition disabled:opacity-60 disabled:cursor-not-allowed ${
                          isAlreadyApplied
                            ? 'bg-emerald-500/15 text-emerald-50 border-emerald-400/35'
                            : isReviewOnly
                              ? 'bg-white/10 hover:bg-white/15 text-white border-white/20'
                              : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border-amber-400/40'
                        }`}
                      >
                        {isAlreadyApplied ? (
                          'Ya aplicaste esta sugerencia'
                        ) : isApplyingSuggestion(currentOptimization?.id) ? (
                          <>
                            <Loader className="w-3.5 h-3.5 shrink-0 animate-spin inline mr-1.5" aria-hidden />
                            Aplicando…
                          </>
                        ) : (
                          currentOptimization?.action_label || (isReviewOnly
                            ? (isMetaReviewOnly ? 'Ver en Meta' : 'Ver en Google')
                            : 'Aplicar')
                        )}
                      </button>
                    </div>
                  );
                })()}

                {criteria.length && !isMetaN8 && !isMetaN9 && !isMetaN10 ? (
                  <CriteriaWithBars
                    title="Por qué califica"
                    criteria={criteria}
                    metrics={
                      currentOptimization?.metrics_extended && typeof currentOptimization.metrics_extended === 'object'
                        ? currentOptimization.metrics_extended
                        : null
                    }
                    currency={currency}
                    intentTone={intent}
                    accountBenchmarks={currentOptimization?.account_benchmarks || null}
                  />
                ) : null}

                {isMetaN8 && cpaWindows ? (
                  <CpaWindowsChart windowComparison={cpaWindows} targetCpa={targetCpa} currency={currency} />
                ) : null}
                {isMetaN10 && cpaWindows ? (
                  <CpaWindowsChart windowComparison={cpaWindows} targetCpa={targetCpa} currency={currency} />
                ) : null}
                {isMetaN9 && roasWindows ? (
                  <RoasWindowsChart windowComparison={roasWindows} targetRoas={targetRoas} />
                ) : null}

                {!effectiveMulti && !isMetaN8 && !isMetaN9 && !isMetaN10 ? (
                  <ContextMetricsBar
                    analysisWindow={currentOptimization?.evidence?.analysis_window || null}
                    metrics={
                      (visibleItems[0]?.metrics_extended && typeof visibleItems[0].metrics_extended === 'object')
                        ? visibleItems[0].metrics_extended
                        : ((visibleItems[0]?.metrics && typeof visibleItems[0].metrics === 'object')
                          ? visibleItems[0].metrics
                          : (currentOptimization?.metrics_extended || {}))
                    }
                    currency={currency}
                    metricKeysToShow={metricKeysToShow}
                    className="mt-3"
                  />
                ) : null}

                {(isMetaN8 || isMetaN9 || isMetaN10) && currentOptimization?.evidence?.analysis_window ? (
                  <div className="mt-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-white/60">
                    {(() => {
                      const aw = currentOptimization.evidence.analysis_window;
                      const start = aw?.start ? new Date(aw.start) : null;
                      const end = aw?.end ? new Date(aw.end) : null;
                      if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                        const fmt = new Intl.DateTimeFormat('es-UY', { day: 'numeric', month: 'short' });
                        return `Período: Del ${fmt.format(start)} al ${fmt.format(end)}`;
                      }
                      if (aw?.label) return `Período: ${String(aw.label)}`;
                      return 'Período: —';
                    })()}
                  </div>
                ) : null}

                {/*
                  Pedido de UX: sacar "Ver descripción completa" del arquetipo D.
                  Si vuelve a ser necesario, reintroducirlo detrás de un flag.
                */}

                {itemsCount === 0 ? (
                  <div className="mt-2 text-sm text-white/70">
                    No hay items específicos para esta optimización.
                  </div>
                ) : null}

                {effectiveMulti ? (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setIsDItemsExpanded((v) => !v)}
                      className="w-full flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:bg-white/7 transition-colors"
                    >
                      <div className="text-left min-w-0">
                        <div className="text-xs font-semibold text-white/80">
                          {isDItemsExpanded ? 'Ocultar' : 'Ver'} {itemsCount} {String(entityTypePlural || 'items').toLowerCase()}
                        </div>
                        <div className="mt-0.5 text-[11px] text-white/55 truncate">
                          Navegá por las entidades incluidas en esta optimización
                        </div>
                      </div>
                      <div className="text-white/60 text-lg shrink-0" aria-hidden>
                        {isDItemsExpanded ? '▴' : '▾'}
                      </div>
                    </button>

                    {isDItemsExpanded ? (
                      <div className="mt-2 flex flex-col gap-2">
                        {visibleItems.map((item, idx) => (
                          <ItemCard
                            key={item?.id ?? idx}
                            item={item}
                            suggestionCriteria={criteria}
                            intent={intent}
                            currency={item?.value_change?.currency || currency}
                            suggestionBreadcrumb={Array.isArray(currentOptimization?.primary_entity_breadcrumb) ? currentOptimization.primary_entity_breadcrumb : []}
                            isApplied={Boolean(item?.applied_at)}
                            onApply={null}
                            suggestionMetricKeysToShow={metricKeysToShow}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })()}
              </React.Fragment>
            );
          })}

            {groupMode ? (
              (isSplitTopSellersMergedView || isKeywordOrSearchTermMergedView) ? (
                <>
                  <div className="mb-3 flex justify-end">
                    <button
                      type="button"
                      disabled={isSplitTopSellersMergedView ? false : (!hasSelectedMergedRows || isApplyingMergedSelection)}
                      onClick={() => {
                        if (isSplitTopSellersMergedView) {
                          // Review-only: abrir Google Ads sin pegarle a /apply
                          const first = selectedMergedRows?.[0]?.opt || suggestionsInView?.[0] || null;
                          if (!first) return;
                          openSuggestionDestination(first, { campaignId: first?.campaign_groups?.[0]?.campaign_id ?? null });
                          return;
                        }
                        applySelectedMergedRows();
                      }}
                      className={`min-h-[36px] rounded-lg px-3 py-2 text-xs font-semibold border transition disabled:opacity-50 ${
                        isSplitTopSellersMergedView
                          ? 'bg-white/10 hover:bg-white/15 text-white border-white/20'
                          : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border-amber-400/40'
                      }`}
                    >
                      {isSplitTopSellersMergedView ? (
                        currentOptimization?.action_label || 'Ver en Google'
                      ) : (
                        isApplyingMergedSelection
                          ? (<><Loader className="w-3.5 h-3.5 shrink-0 animate-spin inline mr-1.5" aria-hidden />Aplicando…</>)
                          : selectedMergedRows.some((row) => isExecutableActionType(row?.opt?.action_type))
                            ? <><Zap className="w-3.5 h-3.5 inline mr-1" />Aplicar y ejecutar ({selectedMergedRows.length})</>
                            : `Aplicar seleccionadas${hasSelectedMergedRows ? ` (${selectedMergedRows.length})` : ''}`
                      )}
                    </button>
                  </div>
                  <div className="mb-4 overflow-x-auto backdrop-blur-md bg-white/10 border border-white/20 rounded-xl p-4">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/30">
                          {!isSplitTopSellersMergedView ? (
                            <th className="w-8 px-2 py-2 text-center">
                              <input
                                type="checkbox"
                                checked={allMergedRowsSelected}
                                onChange={toggleSelectAllMergedRows}
                                className="h-3.5 w-3.5 rounded border-white/30 bg-white/10 text-amber-400 focus:ring-amber-400/60"
                                aria-label="Seleccionar todas las filas"
                              />
                            </th>
                          ) : null}
                          <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                            {isSplitTopSellersMergedView ? 'Producto' : 'Término / Keyword'}
                          </th>
                          {showKeywordMatchTypeColumn && (
                            <th className="px-3 py-2 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[7.5rem]">
                              Concordancia
                            </th>
                          )}
                          <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">Campaña</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-white uppercase tracking-wider">Gasto</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-white uppercase tracking-wider">ROAS</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-white uppercase tracking-wider">CPA</th>
                          <th className="px-4 py-2 text-center text-xs font-bold text-white uppercase tracking-wider">Conv.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/20">
                        {mergedTableRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={
                                // Base columns: selection + entity + campaign + spend + roas + cpa + conv
                                // If selection is hidden, subtract 1.
                                (showKeywordMatchTypeColumn ? 8 : 7) - (isSplitTopSellersMergedView ? 1 : 0)
                              }
                              className="px-4 py-6 text-center text-sm text-white/60"
                            >
                              {isSplitTopSellersMergedView ? 'No hay productos para mostrar.' : 'No hay términos o keywords para mostrar.'}
                            </td>
                          </tr>
                        ) : (
                          mergedTableRows.map((row, idx) => (
                            (() => {
                              const rowCurrencyCode = getCurrencyCodeForSuggestion(row.opt);
                              return (
                            <tr key={`${row.campaign}-${row.entity}-${idx}`} className="hover:bg-white/10 transition-colors">
                              {!isSplitTopSellersMergedView ? (
                                <td className="px-2 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedMergedRowIds.has(row.rowId)}
                                    onChange={() => toggleMergedRowSelection(row.rowId)}
                                    className="h-3.5 w-3.5 rounded border-white/30 bg-white/10 text-amber-400 focus:ring-amber-400/60"
                                    aria-label={`Seleccionar fila ${row.entity}`}
                                  />
                                </td>
                              ) : null}
                              <td className="px-4 py-2 text-white text-sm">
                                <span className="truncate block min-w-0" title={row.entity}>{row.entity}</span>
                              </td>
                              {showKeywordMatchTypeColumn && (
                                <td className="px-3 py-2 align-middle">
                                  {(() => {
                                    const at = String(row.opt?.action_type || '').trim().toUpperCase();
                                    const needsMt =
                                      isGoogleAdsPlatformUi(row.opt?.platform) &&
                                      (at === 'ADD_KEYWORD' || at === 'ADD_NEGATIVE_KEYWORD' || at === 'CHANGE_KEYWORD_MATCH_TYPE');
                                    if (!needsMt) {
                                      return <span className="text-xs text-white/35">—</span>;
                                    }
                                    return (
                                      <select
                                        value={mergedRowMatchTypeByRowId[row.rowId] ?? 'BROAD'}
                                        onChange={(e) => setMergedRowMatchType(row.rowId, e.target.value)}
                                        className="w-full min-w-[6.5rem] max-w-[9rem] h-8 rounded-lg bg-white/10 border border-white/20 px-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                                        aria-label={`Concordancia para ${row.entity}`}
                                      >
                                        <option value="BROAD">Amplia</option>
                                        <option value="PHRASE">Frase</option>
                                        <option value="EXACT">Exacta</option>
                                      </select>
                                    );
                                  })()}
                                </td>
                              )}
                              <td className="px-4 py-2 text-white/90 text-sm">
                                <span className="truncate block min-w-0" title={row.campaign}>{row.campaign}</span>
                              </td>
                              <td className="px-4 py-2 text-center text-white/90 text-sm">{formatCurrency(row.metrics.spend, rowCurrencyCode)}</td>
                              <td className="px-4 py-2 text-center text-sm">
                                <span className={getRoasCellClassName(row.item, row.opt)}>
                                  {formatRatioAsPercent(row.metrics.roas)}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-center text-sm">
                                <span className={getCpaCellClassName(row.item, row.opt)}>
                                  {formatCurrency(row.metrics.cpa, rowCurrencyCode)}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-center text-sm text-white/80">{formatConversions(row.metrics.conversions)}</td>
                            </tr>
                              );
                            })()
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                isDiagnosticMetaRule && diagConfig ? (
                  <div className="mb-6">
                    <div className="text-base sm:text-[15px] font-semibold text-white leading-snug">
                      {diagConfig.title}
                    </div>
                    <div className="mt-1 text-sm text-white/70 leading-relaxed">
                      {diagConfig.description}
                    </div>

                    <div className="mt-4 pt-4 border-t border-white/10">
                      {(() => {
                        const sortedAll = getSortedDiagSuggestions();
                        const showAll = !!showAllDiagByRule?.[normalizedRuleKey];
                        const visible = showAll ? sortedAll : sortedAll.slice(0, 3);
                        const hiddenCount = Math.max(0, sortedAll.length - visible.length);

                        return (
                          <>
                            <div className="space-y-3">
                              {visible.map((opt) => {
                                const suggestionCurrencyCode = getCurrencyCodeForSuggestion(opt);
                                const adName = opt?.primary_entity_name || 'Anuncio';
                                const campaignName = opt?.campaign_name || 'Campaña';
                                const features = opt?.metrics_extended || {};
                                const before = opt?.value_before;
                                const after = opt?.value_after;
                                const isApplying = isApplyingSuggestion(opt?.id);
                                const isViewed = !!viewedDiagIds?.[opt?.id];
                                const adExternalId = opt?.primary_entity_external_id;
                                const actionType = String(opt?.action_type || '').trim().toUpperCase();
                                const isRefreshCreative = actionType === 'REFRESH_CREATIVE';
                                const isSwitchToAdvantagePlus = actionType === 'SWITCH_TO_ADVANTAGE_PLUS';
                                const isMetaReviewOnly = isRefreshCreative || isSwitchToAdvantagePlus || isMetaReviewOnlyActionType(actionType);
                                const resolvedCtaLabel =
                                  typeof diagConfig?.ctaLabel === 'function' ? diagConfig.ctaLabel(opt) : diagConfig?.ctaLabel;
                                const isApplyCta = resolvedCtaLabel === 'Aplicar';
                                const optStatusUpper = String(opt?.status || '').toUpperCase();
                                const canShowApplyCta = !opt?.status || ['NEW', 'SHOWN'].includes(optStatusUpper);
                                const campaignId = opt?.campaign_groups?.[0]?.campaign_id ?? null;

                                return (
                                  <div key={opt.id} className="rounded-xl border border-white/15 bg-white/5 p-3 h-[124px] flex flex-col justify-between overflow-hidden">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="text-sm font-semibold text-white truncate" title={adName}>
                                          {adName}
                                        </div>
                                        <div className="text-xs text-white/55 truncate" title={campaignName}>
                                          {campaignName}
                                        </div>
                                      </div>
                                      {(isApplyCta && !canShowApplyCta) ? null : (
                                        <button
                                          type="button"
                                          disabled={isApplyCta ? isApplying : isViewed}
                                          onClick={async () => {
                                            const adsManagerPayload = {
                                              opt,
                                              adExternalId,
                                              entityName: adName,
                                              dateStart: temporalContext?.recentStartDate ?? null,
                                              dateEnd: temporalContext?.recentEndDate ?? null,
                                              suggestionId: opt?.id,
                                              campaignId,
                                            };
                                            if (isExecutableActionType(opt?.action_type)) {
                                              await applyAndOpenDestination(opt, adsManagerPayload);
                                              return;
                                            }
                                            if (isMetaReviewOnly || !isApplyCta) {
                                              diagConfig.onCta(adsManagerPayload);
                                              return;
                                            }
                                            await applySuggestion(opt, { execute: false });
                                            diagConfig.onCta(adsManagerPayload);
                                          }}
                                          className={`shrink-0 rounded-lg px-3 py-2 text-xs font-semibold border transition disabled:opacity-50 ${
                                            isApplyCta
                                              ? `bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border-amber-400/40`
                                              : `bg-white/10 hover:bg-white/15 text-white border-white/20`
                                          }`}
                                        >
                                          {isApplyCta ? (
                                            <>
                                              {isApplying ? (
                                                <Loader className="w-3.5 h-3.5 shrink-0 animate-spin inline mr-1.5" aria-hidden />
                                              ) : null}
                                              {isApplying ? 'Aplicando…' : 'Aplicar'}
                                            </>
                                          ) : (
                                            isViewed ? 'Vista' : (resolvedCtaLabel || 'Ver')
                                          )}
                                        </button>
                                      )}
                                    </div>

                                    <div className="mt-3 grid grid-cols-2 gap-3 min-h-[52px]">
                                      <div className="flex items-end gap-4">
                                        {diagConfig.metrics.map((m) => {
                                          const current = features?.[m.field] ?? null;
                                          const prev = features?.[m.prev] ?? null;
                                          const deltaPct = getDeltaPct(current, prev);

                                          let valueText = '-';
                                          if (m.format === 'currency') valueText = formatCurrencyNoDecimals(current, suggestionCurrencyCode);
                                          else if (m.format === 'roas') valueText = (current === 0 || current === 0.0) ? '0.0x' : renderRoas(current);
                                          else if (m.format === 'percentage') valueText = (current === 0 || current === 0.0) ? '0.0%' : renderCtr(current);
                                          else if (m.format === 'integer') valueText = current === null || current === undefined ? '-' : `${Math.round(Number(current))}`;
                                          else if (m.format === 'decimal') valueText = renderFrequency(current);

                                          return (
                                            <div key={m.field}>
                                              <div className="text-[10px] text-white/50 uppercase tracking-wider">{m.label}</div>
                                              <div className="text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                                                {valueText}
                                                {renderDeltaBadge(
                                                  deltaPct,
                                                  m.upIsGood,
                                                  buildDeltaTooltip({
                                                    metricLabel: m.label,
                                                    format: m.format,
                                                    current,
                                                    prev,
                                                    deltaPct,
                                                    opt,
                                                    temporalContext,
                                                    currencyCode: suggestionCurrencyCode,
                                                  })
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {diagConfig.showBudget ? (
                                        <div className="flex items-end justify-end gap-3">
                                          <div className="text-right">
                                            <div className="text-[10px] text-white/50 uppercase tracking-wider">Presupuesto diario</div>
                                            {before != null && after != null ? (
                                              <div className="text-sm tabular-nums whitespace-nowrap">
                                                <span className="text-white/50 line-through mr-2">{formatCurrencyNoDecimals(before, suggestionCurrencyCode)}</span>
                                                <span className={`${diagConfig.budgetSuggestedClass} font-semibold`}>
                                                  {isBudgetActionType(opt) && isExecutableActionType(opt?.action_type) ? (
                                                    <input
                                                      inputMode="numeric"
                                                      type="number"
                                                      min="0"
                                                      step="1"
                                                      value={valueAfterBySuggestionId?.[opt?.id] ?? ''}
                                                      onChange={(e) =>
                                                        setValueAfterBySuggestionId((prev) => ({
                                                          ...(prev || {}),
                                                          [opt?.id]: e.target.value === '' ? '' : String(Math.round(Number(e.target.value))),
                                                        }))
                                                      }
                                                      className="w-[96px] h-8 rounded-lg bg-white/10 border border-white/20 px-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-amber-400/40 text-right"
                                                      aria-label="Presupuesto sugerido"
                                                    />
                                                  ) : (
                                                    formatCurrencyNoDecimals(after, suggestionCurrencyCode)
                                                  )}
                                                </span>
                                              </div>
                                            ) : (
                                              <div className="text-sm tabular-nums whitespace-nowrap text-white/35">-</div>
                                            )}
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {hiddenCount > 0 && (
                              <div className="mt-3">
                                <button
                                  type="button"
                                  onClick={() => setShowAllDiagByRule((prev) => ({ ...(prev || {}), [normalizedRuleKey]: true }))}
                                  className="text-xs font-semibold text-white/70 hover:text-white underline underline-offset-4"
                                >
                                  Mostrar más ({hiddenCount})
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}

                    </div>
                  </div>
                ) : ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].includes(String(currentOptimization?.archetype || '').toUpperCase()) ? null : (
                  <div className="space-y-2 mb-6" role="region" aria-label="Sugerencias del grupo">
                    {suggestionsInView.map((opt) => {
                      const isApplying = isApplyingSuggestion(opt?.id);
                      const summaryMetrics = resolveCardSummaryMetrics(opt);
                      const suggestionCurrencyCode = getCurrencyCodeForSuggestion(opt);
                      const actionChangeFormat = inferSuggestionChangeFormat(opt);
                      const beforeBudget = opt?.value_before;
                      const afterBudget = opt?.value_after;
                      const showBudgetInput = isBudgetActionType(opt) && isExecutableActionType(opt?.action_type);
                      const rawBudget = opt?.id != null ? valueAfterBySuggestionId?.[opt?.id] : '';
                      const entityName = opt?.primary_entity_name || opt?.campaign_name || 'Sugerencia';
                      const campaignName = opt?.campaign_name || 'Campaña';
                      const statusUpper = String(opt?.status || '').toUpperCase();
                      const isAlreadyApplied = statusUpper === 'APPLIED';
                      const summaryMetricItems = [
                        { label: 'Gasto', value: formatCurrencyNoDecimals(summaryMetrics?.spend, suggestionCurrencyCode) },
                        { label: 'ROAS', value: formatRatioAsPercent(summaryMetrics?.roas) },
                        { label: 'CPA', value: formatCurrencyNoDecimals(summaryMetrics?.cpa, suggestionCurrencyCode) },
                        { label: 'Conv.', value: formatConversions(summaryMetrics?.conversions) },
                      ];
                      const actionLabel = opt?.action || 'Acción sugerida';
                      const reasonChipText = (() => {
                        const raw = typeof opt?.description === 'string' ? opt.description.trim() : '';
                        if (!raw) return null;
                        // Truncado visual, sin inventar contenido.
                        return raw.length > 64 ? `${raw.slice(0, 64).trim()}…` : raw;
                      })();
                      const reasonTone = getIntentTone(opt?.intent);
                      const hasBudgetChange = beforeBudget != null || afterBudget != null;
                      const actionTypeUpper = String(opt?.action_type || '').trim().toUpperCase();
                      const isMetaReviewOnly = isMetaReviewOnlyActionType(actionTypeUpper);
                      const isGoogleReviewOnly = isGoogleReviewOnlyActionType(actionTypeUpper);
                      const isReviewOnly = isMetaReviewOnly || isGoogleReviewOnly;
                      return (
                        <div key={opt.id} className="rounded-xl border border-white/15 bg-white/5 p-3 h-[124px] flex flex-col justify-between overflow-hidden">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-white truncate" title={entityName}>
                                {entityName}
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-2 min-w-0">
                                <div className="text-xs text-white/55 truncate min-w-0" title={campaignName}>
                                  {campaignName}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center shrink-0">
                              <button
                                type="button"
                                disabled={isAlreadyApplied || isApplying}
                                onClick={() => {
                                  if (isReviewOnly) {
                                    openSuggestionDestination(opt, { entityName });
                                    return;
                                  }
                                  if (isAlreadyApplied) return;
                                  applyAndOpenDestination(opt, { entityName });
                                }}
                                className={`min-h-[36px] rounded-lg px-3 py-2 text-xs font-semibold border transition disabled:opacity-60 disabled:cursor-not-allowed ${
                                  isAlreadyApplied
                                    ? 'bg-emerald-500/15 text-emerald-50 border-emerald-400/35'
                                    : isReviewOnly
                                      ? 'bg-white/10 hover:bg-white/15 text-white border-white/20'
                                      : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border-amber-400/40'
                                }`}
                              >
                                {isAlreadyApplied ? (
                                  'Ya aplicaste'
                                ) : isApplying ? (
                                  <>
                                    <Loader className="w-3.5 h-3.5 shrink-0 animate-spin inline mr-1.5" aria-hidden />
                                    Aplicando…
                                  </>
                                ) : (
                                  opt?.action_label || (isMetaReviewOnly
                                    ? 'Ver en Meta'
                                    : isGoogleReviewOnly
                                      ? 'Ver en Google'
                                      : isExecutableActionType(opt?.action_type)
                                        ? (<><Zap className="w-3.5 h-3.5 inline mr-1" />Aplicar y ejecutar</>)
                                        : 'Aplicar')
                                )}
                              </button>
                            </div>
                          </div>

                          <div className="mt-3 grid grid-cols-2 gap-3 min-h-[52px]">
                            <div className="flex items-end gap-4">
                              {summaryMetricItems.map((metric) => (
                                <div key={metric.label}>
                                  <div className="text-[10px] text-white/50 uppercase tracking-wider">{metric.label}</div>
                                  <div className="text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                                    {metric.value}
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="flex items-end justify-end">
                              <div className="text-right">
                                {!isMetaReviewOnly ? (
                                  reasonChipText ? (
                                    <span
                                      className="inline-flex max-w-[220px] px-2.5 py-1 rounded-full text-[10px] font-semibold border border-white/10 truncate"
                                      style={{ background: reasonTone.bg, color: reasonTone.text }}
                                      title={typeof opt?.description === 'string' ? opt.description : undefined}
                                    >
                                      {reasonChipText}
                                    </span>
                                  ) : (
                                    <div className="text-[10px] text-white/50 uppercase tracking-wider truncate max-w-[180px]" title={actionLabel}>
                                      {actionLabel}
                                    </div>
                                  )
                                ) : null}
                                {hasBudgetChange ? (
                                  <div className="text-sm tabular-nums whitespace-nowrap">
                                    {beforeBudget != null ? (
                                      <span className="text-white/50 line-through mr-2">
                                        {formatCardChangeValue(beforeBudget, actionChangeFormat, suggestionCurrencyCode)}
                                      </span>
                                    ) : null}
                                    {afterBudget != null ? (
                                      <span className={`${getActionAfterValueClass({
                                        label: opt?.action,
                                        before: beforeBudget,
                                        after: afterBudget,
                                      })} font-semibold`}>
                                        {showBudgetInput ? (
                                          <input
                                            inputMode="numeric"
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={rawBudget ?? ''}
                                            onChange={(e) =>
                                              setValueAfterBySuggestionId((prev) => ({
                                                ...(prev || {}),
                                                [opt?.id]: e.target.value === '' ? '' : String(Math.round(Number(e.target.value))),
                                              }))
                                            }
                                            className="w-[96px] h-8 rounded-lg bg-white/10 border border-white/20 px-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-amber-400/40 text-right"
                                            aria-label="Presupuesto sugerido"
                                          />
                                        ) : (
                                          formatCardChangeValue(afterBudget, actionChangeFormat, suggestionCurrencyCode)
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-white/50">-</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-sm tabular-nums whitespace-nowrap text-white/50">-</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )
            ) : (
              <>
                {!['A', 'D', 'E'].includes(String(currentOptimization?.archetype || '').toUpperCase()) ? (
                  <div className="mb-4 flex justify-end">
                    {(() => {
                    const actionType = String(currentOptimization?.action_type || '').trim().toUpperCase();
                    const isMetaReviewOnly = isMetaReviewOnlyActionType(actionType);
                    const isGoogleReviewOnly = isGoogleReviewOnlyActionType(actionType);
                    const isReviewOnly = isMetaReviewOnly || isGoogleReviewOnly;
                    const ctaLabel = currentOptimization?.action_label || (isMetaReviewOnly ? 'Ver en Meta' : isGoogleReviewOnly ? 'Ver en Google' : 'Aplicar');
                    const statusUpper = String(currentOptimization?.status || '').toUpperCase();
                    const canShowApplyCta = !currentOptimization?.status || ['NEW', 'SHOWN'].includes(statusUpper);
                    const isAlreadyApplied = statusUpper === 'APPLIED';
                    if (!isReviewOnly && !canShowApplyCta && !isAlreadyApplied) return null;
                    return (
                      <div className="flex flex-col items-end gap-2">
                        <button
                          type="button"
                          disabled={isAlreadyApplied || isApplyingSuggestion(currentOptimization?.id)}
                          onClick={() => {
                            if (!currentOptimization) return;
                            if (isAlreadyApplied) return;
                            if (isReviewOnly) {
                              openSuggestionDestination(currentOptimization, {
                                entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                                campaignId: currentOptimization?.campaign_groups?.[0]?.campaign_id ?? null,
                              });
                              return;
                            }
                            applyAndOpenDestination(currentOptimization, {
                              entityName: currentOptimization?.primary_entity_name || currentOptimization?.campaign_name || null,
                            });
                          }}
                          className={`min-h-[36px] rounded-lg px-3 py-2 text-xs font-semibold border transition disabled:opacity-60 disabled:cursor-not-allowed ${
                            isAlreadyApplied
                              ? 'bg-emerald-500/15 text-emerald-50 border-emerald-400/35'
                              : isReviewOnly
                              ? 'bg-white/10 hover:bg-white/15 text-white border-white/20'
                              : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border-amber-400/40'
                          }`}
                        >
                          {isAlreadyApplied ? (
                            'Ya aplicaste esta sugerencia'
                          ) : isReviewOnly ? (
                            ctaLabel
                          ) : isApplyingSuggestion(currentOptimization?.id) ? (
                            <>
                              <Loader className="w-3.5 h-3.5 shrink-0 animate-spin inline mr-1.5" aria-hidden />
                              Aplicando…
                            </>
                          ) : isExecutableActionType(currentOptimization?.action_type) ? (
                            <><Zap className="w-3.5 h-3.5 inline mr-1" />Aplicar y ejecutar</>
                          ) : (
                            ctaLabel
                          )}
                        </button>

                        {isAlreadyApplied && currentOptimization?.applied_at ? (
                          <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-[11px] text-emerald-50">
                            <Calendar className="w-3.5 h-3.5 text-emerald-200/90" aria-hidden />
                            <span className="font-medium">
                              Aplicada el {new Date(currentOptimization.applied_at).toLocaleDateString('es-ES')}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    );
                    })()}
                  </div>
                ) : null}
                {!['A', 'D', 'E'].includes(String(currentOptimization?.archetype || '').toUpperCase()) ? (
                  <SuggestionBodyContent
                    opt={currentOptimization}
                    parentSectionTitle={currentOptimization?.campaign_name || ''}
                    getCurrencyCodeForSuggestion={getCurrencyCodeForSuggestion}
                    valueAfter={valueAfterBySuggestionId?.[currentOptimization?.id] ?? ''}
                    onChangeValueAfter={(nextRaw) =>
                      setValueAfterBySuggestionId((prev) => ({ ...(prev || {}), [currentOptimization?.id]: nextRaw }))
                    }
                    keywordMatchType={keywordMatchTypeBySuggestionId?.[currentOptimization?.id] || 'BROAD'}
                    onChangeKeywordMatchType={(next) =>
                      setKeywordMatchTypeBySuggestionId((prev) => ({ ...(prev || {}), [currentOptimization?.id]: next }))
                    }
                    getRoasCellClassName={getRoasCellClassName}
                    getCpaCellClassName={getCpaCellClassName}
                    getRateHandler={getRateHandler}
                    getOrderedUiMetricKeys={getOrderedUiMetricKeys}
                    getUiMetricLabel={getUiMetricLabel}
                    formatUiMetricValue={formatUiMetricValue}
                    formatActionValue={formatActionValue}
                    formatCurrency={formatCurrency}
                    formatRatioAsPercent={formatRatioAsPercent}
                    formatConversions={formatConversions}
                    onApplySuccess={onApplySuccess}
                    setEvidenceModal={setEvidenceModal}
                    ITEM_COLUMN_WIDTH_CLASS={ITEM_COLUMN_WIDTH_CLASS}
                    BASE_UI_METRIC_KEYS={BASE_UI_METRIC_KEYS}
                  />
                ) : null}
              </>
            )}
          </div>
          {groupMode && (
            <div className="pt-3 border-t border-white/10 mt-1">
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                {isMetaReviewOnlyGroupFooter ? (
                  <>
                    {groupHasDismissableSuggestions ? (
                      <button
                        type="button"
                        onClick={handleGroupDismiss}
                        disabled={groupDiscardSubmitting}
                        className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg border border-white/20 bg-white/10 text-white/85 hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {groupDiscardSubmitting ? (
                          <Loader className="w-4 h-4 shrink-0 animate-spin mr-2" aria-hidden />
                        ) : null}
                        <span className="text-xs sm:text-sm font-semibold">Descartar</span>
                      </button>
                    ) : null}
                    {groupHasCommitableSuggestions ? (
                      <button
                        type="button"
                        disabled={groupCommitSubmitting}
                        onClick={async () => {
                          if (groupCommitSubmitting) return;
                          const ids = (suggestionsInView || [])
                            .filter((s) => {
                              const st = String(s?.status || '').toUpperCase();
                              return !s?.status || ['NEW', 'SHOWN'].includes(st);
                            })
                            .map((s) => s?.id)
                            .filter(Boolean);
                          if (ids.length === 0) return;
                          setGroupCommitSubmitting(true);
                          try {
                            await Promise.all(ids.map((id) => patchRequest(`/api/suggestions/${id}/apply`, { execute: false })));
                            ids.forEach((id) => onApplySuccess?.(id, 'APPLIED'));
                            toast.success('Listo. Marcado como “lo voy a hacer”.');
                          } catch (err) {
                            console.warn('No se pudo marcar como aplicada la sugerencia', err);
                            toast.error('No se pudo marcar como aplicada. Intentá de nuevo.');
                          } finally {
                            setGroupCommitSubmitting(false);
                          }
                        }}
                        className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg border border-emerald-400/45 bg-gradient-to-r from-emerald-500/20 to-teal-500/15 text-emerald-50 hover:from-emerald-500/30 hover:to-teal-500/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {groupCommitSubmitting ? (
                          <Loader className="w-4 h-4 shrink-0 animate-spin mr-2" aria-hidden />
                        ) : null}
                        <span className="text-xs sm:text-sm font-semibold">
                          {groupCommitSubmitting ? 'Marcando…' : '¡Lo voy a hacer!'}
                        </span>
                      </button>
                    ) : null}
                  </>
                ) : (
                  <>
                    {groupHasDismissableSuggestions ? (
                      <button
                        type="button"
                        onClick={handleGroupDismiss}
                        disabled={groupDiscardSubmitting}
                        className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg border border-white/20 bg-white/10 text-white/85 hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:opacity-60 disabled:cursor-not-allowed"
                        title="Descartar todas las sugerencias del grupo"
                      >
                        {groupDiscardSubmitting ? (
                          <Loader className="w-4 h-4 shrink-0 animate-spin mr-2" aria-hidden />
                        ) : null}
                        <span className="text-xs sm:text-sm font-semibold">Descartar</span>
                      </button>
                    ) : null}
                    {groupAppliedAt ? (
                      <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-[11px] text-emerald-50">
                        <Calendar className="w-3.5 h-3.5 text-emerald-200/90" aria-hidden />
                        <span className="font-medium">
                          Aplicada el {new Date(groupAppliedAt).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                    ) : null}
                    {isSplitTopSellersMergedView ? (
                      groupHasCommitableSuggestions ? (
                        <button
                          type="button"
                          disabled={groupCommitSubmitting}
                          onClick={async () => {
                            if (groupCommitSubmitting) return;
                            const ids = (suggestionsInView || [])
                              .filter((s) => {
                                const st = String(s?.status || '').toUpperCase();
                                return !s?.status || ['NEW', 'SHOWN'].includes(st);
                              })
                              .map((s) => s?.id)
                              .filter(Boolean);
                            if (ids.length === 0) return;
                            setGroupCommitSubmitting(true);
                            try {
                              await Promise.all(ids.map((id) => patchRequest(`/api/suggestions/${id}/apply`, { execute: false })));
                              ids.forEach((id) => onApplySuccess?.(id, 'APPLIED'));
                              toast.success('Listo. Marcado como “lo voy a hacer”.');
                            } catch (err) {
                              console.warn('No se pudo marcar como aplicada la sugerencia', err);
                              toast.error('No se pudo marcar como aplicada. Intentá de nuevo.');
                            } finally {
                              setGroupCommitSubmitting(false);
                            }
                          }}
                          className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-lg border border-emerald-400/45 bg-gradient-to-r from-emerald-500/20 to-teal-500/15 text-emerald-50 hover:from-emerald-500/30 hover:to-teal-500/25 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {groupCommitSubmitting ? (
                            <Loader className="w-4 h-4 shrink-0 animate-spin mr-2" aria-hidden />
                          ) : null}
                          <span className="text-xs sm:text-sm font-semibold">
                            {groupCommitSubmitting ? 'Marcando…' : '¡Lo voy a hacer!'}
                          </span>
                        </button>
                      ) : null
                    ) : null}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    <EvidenceTableModal
      isOpen={!!evidenceModal}
      onClose={() => setEvidenceModal(null)}
      tables={evidenceModal ?? []}
    />
    {pendingHReview ? (
      <div
        className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="h-review-confirm-title"
      >
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900 text-white shadow-2xl">
          <div className="px-5 pt-5 pb-3">
            <h3 id="h-review-confirm-title" className="text-base font-semibold">
              Confirmar revisión humana
            </h3>
            <p className="mt-2 text-sm text-white/75">
              {pendingHReview.opt?.structural_change?.change_label
                ? `Este cambio (${pendingHReview.opt.structural_change.change_label}) requiere revisión humana antes de aplicarse.`
                : 'Este cambio requiere revisión humana antes de aplicarse.'}
              {' '}Confirmá que revisaste la propuesta y querés marcarla como aplicada.
            </p>
          </div>
          <div className="px-5 pb-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setPendingHReview(null)}
              className="px-3 py-1.5 rounded-lg border border-white/15 text-sm text-white/85 hover:bg-white/5"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmPendingHReview}
              className="px-3 py-1.5 rounded-lg border border-emerald-400/45 bg-emerald-500/20 text-sm font-semibold text-emerald-50 hover:bg-emerald-500/30"
            >
              Sí, ya lo revisé
            </button>
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
};

export default OptimizationDetailModalV2;
